import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { NIFTY_AI } from '../../data/mock';
import { getNiftyHistory, getNiftyWeekly, getIndices, triggerNiftyBackfill, getBreadthIntraday, triggerEquityFiiBackfill, getEquityFiiBackfillStatus } from '../../api/client';
import { useSidePanel } from '../../context/SidePanelContext';
import { getNiftyAI, generateTabReport } from '../../api/ai';
import { NiftyAISection, fmtIst, computeAlignmentSummary } from '../ai/NiftyAISection';
import { getNiftyDiagnostics, getNiftyMonthly } from '../../api/client';
import RefreshIcon from '../ui/RefreshIcon';

/* ── Cache layer (two tiers) ─────────────────────────────────────────────────
 *  Tier 1 — module-level (_cache): survives SPA tab switches (in-memory, fast)
 *  Tier 2 — sessionStorage: survives F5 / hard refresh within same browser tab
 *            30-min TTL; ~150 KB for 500 rows — well within the 5 MB limit
 * ─────────────────────────────────────────────────────────────────────────── */
const _cache = {
  history: null,   // { data: [...], ts: epoch_ms }
  weekly:  null,   // { data: [...], ts: epoch_ms }
};
const CACHE_TTL     = 5  * 60 * 1000;
const STORAGE_TTL   = 30 * 60 * 1000;
const SS_KEY_HIST   = 'nifty_hist_v1';
const SS_KEY_WEEKLY = 'nifty_weekly_v1';

function _cacheValid(entry) {
  return entry !== null && (Date.now() - entry.ts) < CACHE_TTL;
}
function _cacheSet(key, data) {
  _cache[key] = { data, ts: Date.now() };
}
function _cacheClear() {
  _cache.history = null;
  _cache.weekly  = null;
  try { sessionStorage.removeItem(SS_KEY_HIST);   } catch {}
  try { sessionStorage.removeItem(SS_KEY_WEEKLY); } catch {}
}

function _ssLoad(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > STORAGE_TTL) return null;
    return data;
  } catch { return null; }
}
function _ssSave(key, data) {
  try { sessionStorage.setItem(key, JSON.stringify({ data, ts: Date.now() })); } catch {}
}

/* ── Market status from IST clock ── */
function computeMktStatus() {
  const now = new Date();
  const ist = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  const d = ist.getDay();
  if (d === 0 || d === 6) return 'CLOSED';
  const mins = ist.getHours() * 60 + ist.getMinutes();
  if (mins >= 9 * 60 + 15 && mins < 15 * 60 + 30) return 'OPEN';
  if (mins >= 9 * 60 && mins < 9 * 60 + 15) return 'PRE-OPEN';
  return 'CLOSED';
}

/* ── Price formatter: XX,XXX.X ── */
const fmtP = v =>
  v == null ? '—' : v.toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/* ── Canvas helpers ── */
function getCSSVar(name) {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

function clearCanvas(canvas) {
  if (!canvas) return;
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  canvas.width  = rect.width  * dpr;
  canvas.height = rect.height * dpr;
  canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
}

/* ── Shared crosshair helper — finds closest index by date ── */
function _closestIdx(dates, targetDate) {
  if (!dates || !dates.length || !targetDate) return 0;
  const target = new Date(targetDate).getTime();
  let best = 0, bestDiff = Infinity;
  dates.forEach((d, i) => {
    if (!d) return;
    const diff = Math.abs(new Date(d).getTime() - target);
    if (diff < bestDiff) { bestDiff = diff; best = i; }
  });
  return best;
}

/* ── Zone fill helper — RSI-style gradient above or below a threshold ── */
function _fillZone(ctx, data, px, py, W, H, pad, threshold, above, hexColor) {
  const thY = py(threshold);
  if (thY <= pad.t - 2 || thY >= H - pad.b + 2) return;  // threshold out of view

  ctx.save();
  ctx.beginPath();
  if (above) {
    ctx.rect(0, 0, W, thY);           // clip to area above threshold
  } else {
    ctx.rect(0, thY, W, H - thY);     // clip to area below threshold
  }
  ctx.clip();

  // Fill the area under the data line — clipped region shows only the zone
  ctx.beginPath();
  data.forEach((d, i) => {
    i === 0 ? ctx.moveTo(px(i), py(d.v)) : ctx.lineTo(px(i), py(d.v));
  });
  ctx.lineTo(px(data.length - 1), thY);
  ctx.lineTo(px(0), thY);
  ctx.closePath();

  // Gradient:
  //  above threshold → saturated at extreme (top), fades toward threshold line
  //  below threshold → transparent near threshold, deepens toward extreme (bottom)
  const extremeY = above
    ? Math.min(...data.map(d => py(d.v)))
    : Math.max(...data.map(d => py(d.v)));
  const grad = ctx.createLinearGradient(0, above ? extremeY : thY, 0, above ? thY : extremeY);
  if (above) {
    grad.addColorStop(0, hexColor + 'CC');  // saturated at extreme top
    grad.addColorStop(1, hexColor + '08');  // fades to near-transparent at threshold
  } else {
    grad.addColorStop(0, hexColor + '0C');  // near-transparent at threshold (0.8 line)
    grad.addColorStop(1, hexColor + 'CC');  // deepens at extreme bottom
  }
  ctx.fillStyle = grad;
  ctx.fill();
  ctx.restore();
}

/* ── Line chart (VIX, PCR, FII%) — supports zones + crosshair ── */
function drawLineChart(canvas, data, opts = {}) {
  if (!canvas) return;
  if (!data || data.length < 2) { clearCanvas(canvas); return; }
  const {
    refLines = [], color = '#1a5aaa' /* --accent-blue */, areaFill = false,
    secondLine = null, zones = [], crosshairDate = null,
  } = opts;
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;
  const ctx = canvas.getContext('2d');
  ctx.scale(dpr, dpr);
  const W = rect.width, H = rect.height;
  const pad = { l:8, r:8, t:6, b:6 };
  const vals = data.map(d => d.v);
  const all = secondLine ? [...vals, ...secondLine.map(d => d.v)] : vals;
  const mn = Math.min(...all), mx = Math.max(...all), rng = mx - mn || 1;
  const px = i => pad.l + (i / (data.length - 1)) * (W - pad.l - pad.r);
  const py = v => pad.t + (1 - (v - mn) / rng) * (H - pad.t - pad.b);

  ctx.clearRect(0, 0, W, H);

  // Reference lines
  refLines.forEach(rl => {
    const ry = py(rl.v);
    if (ry < pad.t || ry > H - pad.b) return;
    ctx.beginPath(); ctx.setLineDash([4, 3]);
    ctx.strokeStyle = rl.c; ctx.lineWidth = 0.8; ctx.globalAlpha = 0.7;
    ctx.moveTo(pad.l, ry); ctx.lineTo(W - pad.r, ry); ctx.stroke();
    ctx.setLineDash([]); ctx.globalAlpha = 1;
    ctx.fillStyle = rl.c; ctx.font = '9px "IBM Plex Sans",system-ui,sans-serif'; ctx.textAlign = 'right';
    ctx.fillText(rl.l, W - pad.r - 2, ry - 2);
  });

  // Zone fills (RSI-style overbought/oversold)
  zones.forEach(({ threshold, above, color: zoneColor }) => {
    _fillZone(ctx, data, px, py, W, H, pad, threshold, above, zoneColor);
  });

  // Area fill under the main line
  if (areaFill) {
    ctx.beginPath();
    data.forEach((d, i) => { i === 0 ? ctx.moveTo(px(i), py(d.v)) : ctx.lineTo(px(i), py(d.v)); });
    ctx.lineTo(px(data.length - 1), H - pad.b);
    ctx.lineTo(px(0), H - pad.b);
    ctx.closePath();
    const grad = ctx.createLinearGradient(0, pad.t, 0, H - pad.b);
    grad.addColorStop(0, color + '30'); grad.addColorStop(1, color + '04');
    ctx.fillStyle = grad; ctx.fill();
  }

  // Second line (dashed)
  if (secondLine) {
    ctx.beginPath();
    secondLine.forEach((d, i) => { i === 0 ? ctx.moveTo(px(i), py(d.v)) : ctx.lineTo(px(i), py(d.v)); });
    ctx.strokeStyle = secondLine.color || '#c8922a' /* --amber */;
    ctx.lineWidth = 1.5; ctx.setLineDash([4, 3]); ctx.lineJoin = 'round';
    ctx.stroke(); ctx.setLineDash([]);
  }

  // Main line
  ctx.beginPath();
  data.forEach((d, i) => { i === 0 ? ctx.moveTo(px(i), py(d.v)) : ctx.lineTo(px(i), py(d.v)); });
  ctx.strokeStyle = color; ctx.lineWidth = 1.6; ctx.lineJoin = 'round'; ctx.stroke();

  // ── Crosshair ──
  if (crosshairDate && data[0]?.date) {
    const dates = data.map(d => d.date);
    const ci = _closestIdx(dates, crosshairDate);
    const cx = px(ci), cy = py(vals[ci]);
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(cx, pad.t); ctx.lineTo(cx, H - pad.b);
    ctx.strokeStyle = 'rgba(128,128,128,0.55)'; ctx.lineWidth = 1;
    ctx.setLineDash([3, 2]); ctx.stroke(); ctx.setLineDash([]);
    ctx.beginPath(); ctx.arc(cx, cy, 3, 0, Math.PI * 2);
    ctx.fillStyle = color; ctx.fill();
    const onRight = cx < W * 0.6;
    ctx.font = 'bold 9px "IBM Plex Mono",monospace';
    ctx.textAlign = onRight ? 'left' : 'right';
    ctx.fillStyle = getCSSVar('--text') || '#222';
    ctx.fillText(vals[ci].toFixed(2), cx + (onRight ? 6 : -6), cy - 5);
    ctx.font = '9px "IBM Plex Sans",system-ui,sans-serif';
    ctx.fillStyle = getCSSVar('--text3') || '#888';
    ctx.fillText(data[ci].date ?? '', cx + (onRight ? 6 : -6), H - 1);
    ctx.restore();
  }
}

/* ── NIFTY price + Futures OI build-up — supports crosshair ── */
function drawNiftyBuildup(canvas, data, crosshairDate) {
  if (!canvas) return;
  if (!data || data.length < 2) { clearCanvas(canvas); return; }
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;
  const ctx = canvas.getContext('2d');
  ctx.scale(dpr, dpr);
  const W = rect.width, H = rect.height;

  const hasOI = data.some(d => (d.futOI || 0) > 0);
  const priceH = hasOI ? H * 0.60 : H * 0.88;
  const oiH    = H * 0.27;
  const gap    = H * 0.06;
  const pad    = { l:8, r:8 };

  const closes = data.map(d => d.close);
  const mn = Math.min(...closes), mx = Math.max(...closes), rng = mx - mn || 1;
  const px = i => pad.l + (i / (data.length - 1)) * (W - pad.l - pad.r);
  const py = v => 4 + (1 - (v - mn) / rng) * (priceH - 8);

  ctx.clearRect(0, 0, W, H);

  // Expiry vertical lines
  data.forEach((d, i) => {
    if (d.isExpiry) {
      ctx.beginPath(); ctx.setLineDash([3, 3]);
      ctx.strokeStyle = '#c8742a66'; ctx.lineWidth = 0.8;
      ctx.moveTo(px(i), 2); ctx.lineTo(px(i), priceH);
      ctx.stroke(); ctx.setLineDash([]);
    }
  });

  // Price line
  ctx.beginPath();
  data.forEach((d, i) => { i === 0 ? ctx.moveTo(px(i), py(d.close)) : ctx.lineTo(px(i), py(d.close)); });
  ctx.strokeStyle = '#1a5aaa' /* --accent-blue */; ctx.lineWidth = 1.6; ctx.lineJoin = 'round'; ctx.stroke();

  if (!hasOI) {
    ctx.fillStyle = getCSSVar('--text3') || '#888';
    ctx.font = '9px "IBM Plex Sans",system-ui,sans-serif';
    ctx.textAlign = 'center';
    ctx.globalAlpha = 0.5;
    ctx.fillText('Futures OI — backfilling…', W / 2, H - 6);
    ctx.globalAlpha = 1;
  } else {
    const BU_COLORS = { LB:'#3f6b4d' /* --up-field-ink */, SB:'#8f4a44' /* --down-field-ink */, SC:'#c8922a' /* --amber */, LU:'#1e4fc2' };
    const barTop = priceH + gap;
    const barW   = Math.max(1, (W - pad.l - pad.r) / data.length - 1);
    const maxOI  = Math.max(...data.map(d => d.futOI || 0));

    data.forEach((d, i) => {
      if (!d.futOI || d.futOI <= 0) return;
      const barH = Math.max(2, (d.futOI / maxOI) * oiH);
      const x = pad.l + i * (W - pad.l - pad.r) / data.length;
      ctx.fillStyle = BU_COLORS[d.buildUp] || '#888';
      ctx.globalAlpha = 0.85;
      if (d.isExpiry) {
        ctx.strokeStyle = '#c8922a' /* --amber */; ctx.lineWidth = 1;
        ctx.strokeRect(x, barTop + oiH - barH, barW, barH);
      }
      ctx.fillRect(x, barTop + oiH - barH, barW, barH);
      ctx.globalAlpha = 1;
    });

    // ── Crosshair ──
    if (crosshairDate && data.length > 0) {
      const dates = data.map(d => d.date);
      const ci = _closestIdx(dates, crosshairDate);
      const cx = px(ci);
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(cx, 2); ctx.lineTo(cx, H - 2);
      ctx.strokeStyle = 'rgba(128,128,128,0.55)'; ctx.lineWidth = 1;
      ctx.setLineDash([3, 2]); ctx.stroke(); ctx.setLineDash([]);

      // Price dot + value
      const cpy = py(data[ci].close);
      ctx.beginPath(); ctx.arc(cx, cpy, 3, 0, Math.PI * 2);
      ctx.fillStyle = '#1a5aaa' /* --accent-blue */; ctx.fill();
      const onRight = cx < W * 0.6;
      const tx = cx + (onRight ? 6 : -6);
      ctx.textAlign = onRight ? 'left' : 'right';
      ctx.font = 'bold 9px "IBM Plex Mono",monospace';
      ctx.fillStyle = getCSSVar('--text') || '#222';
      ctx.fillText(fmtP(data[ci].close), tx, cpy - 5);

      // Build-up type label — floated 14px clear above the hovered OI bar top
      const buildUp = data[ci].buildUp;
      if (buildUp && BU_COLORS[buildUp]) {
        const futOI = data[ci].futOI || 0;
        const barH  = futOI > 0 ? Math.max(2, (futOI / maxOI) * oiH) : 0;
        const barTopY = barTop + oiH - barH;
        ctx.font = 'bold 9px "IBM Plex Mono",monospace';
        ctx.fillStyle = BU_COLORS[buildUp];
        // Float label just above bar top; if bar is very tall, pin just above the OI section
        const idealLabelY = barTopY - 6;
        const labelY = idealLabelY >= barTop + 8 ? idealLabelY : barTop - 3;
        ctx.fillText(buildUp, tx, labelY);
      }

      // Date at bottom
      ctx.font = '9px "IBM Plex Sans",system-ui,sans-serif';
      ctx.fillStyle = getCSSVar('--text3') || '#888';
      ctx.fillText(data[ci].date ?? '', tx, H - 3);
      ctx.restore();
    }
  }

  // Price-only crosshair (no OI data)
  if (!hasOI && crosshairDate && data.length > 0) {
    const dates = data.map(d => d.date);
    const ci = _closestIdx(dates, crosshairDate);
    const cx = px(ci);
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(cx, 2); ctx.lineTo(cx, H - 2);
    ctx.strokeStyle = 'rgba(128,128,128,0.55)'; ctx.lineWidth = 1;
    ctx.setLineDash([3, 2]); ctx.stroke(); ctx.setLineDash([]);
    const cpy = py(data[ci].close);
    ctx.beginPath(); ctx.arc(cx, cpy, 3, 0, Math.PI * 2);
    ctx.fillStyle = '#1a5aaa' /* --accent-blue */; ctx.fill();
    const onRight = cx < W * 0.6;
    const tx = cx + (onRight ? 6 : -6);
    ctx.textAlign = onRight ? 'left' : 'right';
    ctx.font = 'bold 9px "IBM Plex Mono",monospace';
    ctx.fillStyle = getCSSVar('--text') || '#222';
    ctx.fillText(fmtP(data[ci].close), tx, cpy - 5);
    ctx.font = '9px "IBM Plex Sans",system-ui,sans-serif';
    ctx.fillStyle = getCSSVar('--text3') || '#888';
    ctx.fillText(data[ci].date ?? '', tx, H - 3);
    ctx.restore();
  }
}

/* ── Breadth % bar chart — % of NIFTY 50 stocks positive ── */
function drawBreadthChart(canvas, data, crosshairDate) {
  if (!canvas) return;
  if (!data || data.length < 2) { clearCanvas(canvas); return; }
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;
  const ctx = canvas.getContext('2d');
  ctx.scale(dpr, dpr);
  const W = rect.width, H = rect.height;
  const pad = { l:8, r:8, t:6, b:6 };
  const innerH = H - pad.t - pad.b;
  const py50 = pad.t + innerH * 0.5;

  ctx.clearRect(0, 0, W, H);

  // 50% reference line
  ctx.beginPath(); ctx.setLineDash([4, 3]);
  ctx.strokeStyle = 'rgba(128,128,128,0.4)'; ctx.lineWidth = 0.8;
  ctx.moveTo(pad.l, py50); ctx.lineTo(W - pad.r, py50); ctx.stroke(); ctx.setLineDash([]);
  ctx.fillStyle = getCSSVar('--text3') || '#888';
  ctx.font = '9px "IBM Plex Sans",system-ui,sans-serif'; ctx.textAlign = 'right';
  ctx.fillText('50%', W - pad.r - 2, py50 - 2);

  // Additional reference lines at 70% (bullish zone) and 30% (bearish zone)
  [70, 30].forEach(pct => {
    const ry = pad.t + (1 - pct / 100) * innerH;
    ctx.beginPath(); ctx.setLineDash([2, 4]);
    ctx.strokeStyle = pct > 50 ? 'rgba(26,106,26,0.2)' : 'rgba(136,30,16,0.2)';
    ctx.lineWidth = 0.6;
    ctx.moveTo(pad.l, ry); ctx.lineTo(W - pad.r, ry); ctx.stroke(); ctx.setLineDash([]);
    ctx.fillStyle = pct > 50 ? 'rgba(26,106,26,0.5)' : 'rgba(136,30,16,0.5)';
    ctx.font = '9px "IBM Plex Sans",system-ui,sans-serif'; ctx.textAlign = 'right';
    ctx.fillText(`${pct}%`, W - pad.r - 2, ry - 1);
  });

  const n = data.length;
  const barW = Math.max(1, (W - pad.l - pad.r) / n - 0.5);

  data.forEach((d, i) => {
    const adv = d.advDec?.adv ?? 0;
    const dec = d.advDec?.dec ?? 0;
    const total = adv + dec || 1;
    const advPct = adv / total * 100;
    const barH = Math.abs(advPct - 50) / 50 * (innerH * 0.5);
    const x = pad.l + i * (W - pad.l - pad.r) / n;
    const isUp = advPct >= 50;

    ctx.fillStyle = isUp ? 'rgba(26,106,26,0.75)' : 'rgba(136,30,16,0.75)';
    ctx.globalAlpha = 0.85;
    if (isUp) {
      ctx.fillRect(x, py50 - barH, barW, barH);
    } else {
      ctx.fillRect(x, py50, barW, barH);
    }
    ctx.globalAlpha = 1;
  });

  // ── Crosshair ──
  if (crosshairDate) {
    const dates = data.map(d => d.date);
    const ci = _closestIdx(dates, crosshairDate);
    const x = pad.l + ci * (W - pad.l - pad.r) / n + barW / 2;
    ctx.save();
    ctx.beginPath(); ctx.moveTo(x, pad.t); ctx.lineTo(x, H - pad.b);
    ctx.strokeStyle = 'rgba(128,128,128,0.55)'; ctx.lineWidth = 1;
    ctx.setLineDash([3, 2]); ctx.stroke(); ctx.setLineDash([]);

    const adv = data[ci].advDec?.adv ?? 0;
    const dec = data[ci].advDec?.dec ?? 0;
    const total = adv + dec || 1;
    const advPct = adv / total * 100;
    const dotY = pad.t + (1 - advPct / 100) * innerH;
    ctx.beginPath(); ctx.arc(x, dotY, 3, 0, Math.PI * 2);
    ctx.fillStyle = advPct >= 50 ? '#3f6b4d' /* --up-field-ink */ : '#8f4a44' /* --down-field-ink */; ctx.fill();

    const onRight = x < W * 0.6;
    const tx = x + (onRight ? 6 : -6);
    ctx.textAlign = onRight ? 'left' : 'right';
    // Pin A&D + % to the very top of the cursor line — always in clear space
    ctx.font = 'bold 9px "IBM Plex Mono",monospace';
    ctx.fillStyle = advPct >= 50 ? '#3f6b4d' /* --up-field-ink */ : '#8f4a44' /* --down-field-ink */;
    ctx.fillText(`${advPct.toFixed(0)}%`, tx, pad.t + 9);
    ctx.font = '9px "IBM Plex Sans",system-ui,sans-serif';
    ctx.fillStyle = getCSSVar('--text3') || '#888';
    ctx.fillText(`▲${adv} ▼${dec}`, tx, pad.t + 18);
    // Date at bottom
    ctx.fillText(data[ci].date ?? '', tx, H - 1);
    ctx.restore();
  }
}

/* ── EMA helper ── */
function computeEMA(values, period = 20) {
  if (!values || values.length === 0) return [];
  const k = 2 / (period + 1);
  const ema = [values[0]];
  for (let i = 1; i < values.length; i++) {
    ema.push(values[i] * k + ema[i - 1] * (1 - k));
  }
  return ema;
}

/* ── Intraday breadth chart — adv% line + 20 EMA overlay ── */
function drawBreadthIntradayChart(canvas, data, crosshairTime) {
  if (!canvas) return;
  if (!data || data.length < 2) { clearCanvas(canvas); return; }
  const dpr  = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  canvas.width  = rect.width  * dpr;
  canvas.height = rect.height * dpr;
  const ctx = canvas.getContext('2d');
  ctx.scale(dpr, dpr);
  const W = rect.width, H = rect.height;
  const pad = { l:8, r:8, t:6, b:18 };  // extra bottom for time labels
  const innerH = H - pad.t - pad.b;

  // Y: 0–100% adv_pct
  const py = pct => pad.t + (1 - pct / 100) * innerH;

  ctx.clearRect(0, 0, W, H);

  // Reference lines at 70%, 50%, 30%
  [70, 50, 30].forEach(pct => {
    const ry = py(pct);
    ctx.beginPath(); ctx.setLineDash(pct === 50 ? [4, 3] : [2, 4]);
    ctx.strokeStyle = pct === 50
      ? 'rgba(128,128,128,0.4)'
      : pct > 50 ? 'rgba(26,106,26,0.2)' : 'rgba(136,30,16,0.2)';
    ctx.lineWidth = 0.8;
    ctx.moveTo(pad.l, ry); ctx.lineTo(W - pad.r, ry); ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = pct === 50 ? 'rgba(128,128,128,0.5)'
      : pct > 50 ? 'rgba(26,106,26,0.5)' : 'rgba(136,30,16,0.5)';
    ctx.font = '9px "IBM Plex Sans",system-ui,sans-serif'; ctx.textAlign = 'right';
    ctx.fillText(`${pct}%`, W - pad.r - 2, ry - 1);
  });

  const n = data.length;
  const px = i => pad.l + (i / Math.max(n - 1, 1)) * (W - pad.l - pad.r);
  const vals = data.map(d => d.adv_pct);
  const ema20 = computeEMA(vals, 20);

  // Area fill under adv_pct line
  ctx.beginPath();
  data.forEach((d, i) => { i === 0 ? ctx.moveTo(px(i), py(d.adv_pct)) : ctx.lineTo(px(i), py(d.adv_pct)); });
  ctx.lineTo(px(n - 1), H - pad.b);
  ctx.lineTo(px(0), H - pad.b);
  ctx.closePath();
  const grad = ctx.createLinearGradient(0, pad.t, 0, H - pad.b);
  grad.addColorStop(0, 'rgba(26,90,170,0.22)');
  grad.addColorStop(1, 'rgba(26,90,170,0.02)');
  ctx.fillStyle = grad; ctx.fill();

  // adv_pct main line
  ctx.beginPath();
  data.forEach((d, i) => { i === 0 ? ctx.moveTo(px(i), py(d.adv_pct)) : ctx.lineTo(px(i), py(d.adv_pct)); });
  ctx.strokeStyle = '#1a5aaa' /* --accent-blue */; ctx.lineWidth = 1.6; ctx.lineJoin = 'round'; ctx.stroke();

  // 20 EMA — dashed orange
  if (ema20.length >= 20) {
    ctx.beginPath();
    ema20.forEach((v, i) => { i === 0 ? ctx.moveTo(px(i), py(v)) : ctx.lineTo(px(i), py(v)); });
    ctx.strokeStyle = '#c8922a' /* --amber */; ctx.lineWidth = 1.2;
    ctx.setLineDash([4, 3]); ctx.lineJoin = 'round'; ctx.stroke();
    ctx.setLineDash([]);
  }

  // Time labels on x-axis (every ~30 min = 6 bars)
  const labelStep = Math.max(1, Math.floor(n / 8));
  ctx.fillStyle = getCSSVar('--text3') || '#888';
  ctx.font = '9px "IBM Plex Sans",system-ui,sans-serif'; ctx.textAlign = 'center';
  data.forEach((d, i) => {
    if (i % labelStep === 0 || i === n - 1) {
      ctx.fillText(d.time, px(i), H - 2);
    }
  });

  // Crosshair
  const ci = data.findIndex(d => d.time === crosshairTime);
  if (ci >= 0) {
    const cx = px(ci), cy = py(data[ci].adv_pct);
    ctx.save();
    ctx.beginPath(); ctx.moveTo(cx, pad.t); ctx.lineTo(cx, H - pad.b);
    ctx.strokeStyle = 'rgba(128,128,128,0.55)'; ctx.lineWidth = 1;
    ctx.setLineDash([3, 2]); ctx.stroke(); ctx.setLineDash([]);
    ctx.beginPath(); ctx.arc(cx, cy, 3, 0, Math.PI * 2);
    ctx.fillStyle = data[ci].adv_pct >= 50 ? '#3f6b4d' /* --up-field-ink */ : '#8f4a44' /* --down-field-ink */; ctx.fill();
    const onRight = cx < W * 0.6;
    const tx = cx + (onRight ? 6 : -6);
    ctx.textAlign = onRight ? 'left' : 'right';
    ctx.font = 'bold 9px "IBM Plex Mono",monospace';
    ctx.fillStyle = data[ci].adv_pct >= 50 ? '#3f6b4d' /* --up-field-ink */ : '#8f4a44' /* --down-field-ink */;
    ctx.fillText(`${data[ci].adv_pct.toFixed(0)}%`, tx, pad.t + 9);
    ctx.font = '9px "IBM Plex Sans",system-ui,sans-serif';
    ctx.fillStyle = getCSSVar('--text3') || '#888';
    ctx.fillText(`▲${data[ci].adv} ▼${data[ci].dec}`, tx, pad.t + 18);
    ctx.fillText(data[ci].time, tx, H - pad.b + 8);
    ctx.restore();
  }
}

/* ── DII + FII + FII Derivative flows — stacked diverging bar chart ── */
const FLOW_COLORS = { dii: '#1a5aaa' /* --accent-blue */, fii: '#c8922a' /* --amber */, fut: '#1a8080' };
const FLOW_KEYS   = { dii: 'dii', fii: 'fii', fut: 'fiiNetFut' };

function drawFlowsChart(canvas, data, visible, crosshairDate) {
  if (!canvas) return;
  if (!data || data.length < 2) { clearCanvas(canvas); return; }
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;
  const ctx = canvas.getContext('2d');
  ctx.scale(dpr, dpr);
  const W = rect.width, H = rect.height;
  const pad = { l:8, r:8, t:6, b:6 };

  // Compute overall range across all visible series
  let maxSum = 0, minSum = 0;
  data.forEach(d => {
    let pos = 0, neg = 0;
    Object.keys(FLOW_KEYS).forEach(s => {
      if (!visible[s]) return;
      const v = d[FLOW_KEYS[s]] ?? 0;
      if (v > 0) pos += v; else neg += v;
    });
    maxSum = Math.max(maxSum, pos);
    minSum = Math.min(minSum, neg);
  });

  // Guard against empty data
  if (maxSum === 0 && minSum === 0) {
    clearCanvas(canvas);
    return;
  }

  const totalRange = (maxSum - minSum) || 1;
  const innerH = H - pad.t - pad.b;
  const zeroY = pad.t + (maxSum / totalRange) * innerH;
  const py_v = v => zeroY - (v / totalRange) * innerH;

  ctx.clearRect(0, 0, W, H);

  // Zero reference line
  ctx.beginPath(); ctx.setLineDash([4, 3]);
  ctx.strokeStyle = 'rgba(128,128,128,0.4)'; ctx.lineWidth = 0.8;
  ctx.moveTo(pad.l, zeroY); ctx.lineTo(W - pad.r, zeroY); ctx.stroke(); ctx.setLineDash([]);
  ctx.fillStyle = getCSSVar('--text3') || '#888';
  ctx.font = '9px "IBM Plex Sans",system-ui,sans-serif'; ctx.textAlign = 'right';
  ctx.fillText('0', W - pad.r - 2, zeroY - 2);

  const n = data.length;
  const barW = Math.max(1, (W - pad.l - pad.r) / n - 0.5);

  data.forEach((d, i) => {
    const x = pad.l + i * (W - pad.l - pad.r) / n;
    let posStack = 0, negStack = 0;

    ['dii', 'fii', 'fut'].forEach(s => {
      if (!visible[s]) return;
      const v = d[FLOW_KEYS[s]];
      if (v == null || v === 0) return;

      if (v > 0) {
        const bh = (v / totalRange) * innerH;
        ctx.fillStyle = FLOW_COLORS[s];
        ctx.globalAlpha = 0.82;
        ctx.fillRect(x, zeroY - posStack - bh, barW, bh);
        posStack += bh;
      } else {
        const bh = (-v / totalRange) * innerH;
        ctx.fillStyle = FLOW_COLORS[s];
        ctx.globalAlpha = 0.82;
        ctx.fillRect(x, zeroY + negStack, barW, bh);
        negStack += bh;
      }
    });
    ctx.globalAlpha = 1;
  });

  // ── Crosshair ──
  if (crosshairDate) {
    const dates = data.map(d => d.date);
    const ci = _closestIdx(dates, crosshairDate);
    const cx = pad.l + ci * (W - pad.l - pad.r) / n + barW / 2;
    ctx.save();
    ctx.beginPath(); ctx.moveTo(cx, pad.t); ctx.lineTo(cx, H - pad.b);
    ctx.strokeStyle = 'rgba(128,128,128,0.55)'; ctx.lineWidth = 1;
    ctx.setLineDash([3, 2]); ctx.stroke(); ctx.setLineDash([]);

    const d = data[ci];
    const onRight = cx < W * 0.55;
    const tx = cx + (onRight ? 6 : -6);
    ctx.textAlign = onRight ? 'left' : 'right';

    // Pin labels to the top of the cursor line — always in clear white space
    const visibleKeys = Object.keys(FLOW_KEYS).filter(s => visible[s] && d[FLOW_KEYS[s]] != null);
    const lineH = 11, boxPad = 4;
    const boxH  = visibleKeys.length * lineH + boxPad * 2 - 2;
    // Compute box width from actual text measurements
    ctx.font = 'bold 9px "IBM Plex Mono",monospace';
    let maxTW = 0;
    visibleKeys.forEach(s => {
      const v = d[FLOW_KEYS[s]];
      const sign = v >= 0 ? '+' : '';
      const tw = ctx.measureText(`${s.toUpperCase()} ${sign}${Math.round(v)}cr`).width;
      if (tw > maxTW) maxTW = tw;
    });
    const boxW  = maxTW + 14;
    const boxX  = onRight ? cx + 4 : cx - 4 - boxW;
    const boxY  = pad.t + 2;
    // Translucent background
    ctx.save();
    ctx.globalAlpha = 0.70;
    ctx.fillStyle = getCSSVar('--bg') || '#ffffff';
    ctx.fillRect(boxX, boxY, boxW, boxH);
    ctx.globalAlpha = 1;
    ctx.restore();

    ctx.font = 'bold 9px "IBM Plex Mono",monospace';
    ctx.textAlign = onRight ? 'left' : 'right';
    let ty = pad.t + boxPad + lineH - 2;
    visibleKeys.forEach(s => {
      const v = d[FLOW_KEYS[s]];
      ctx.fillStyle = FLOW_COLORS[s];
      const sign = v >= 0 ? '+' : '';
      ctx.fillText(`${s.toUpperCase()} ${sign}${Math.round(v)}cr`, tx, ty);
      ty += lineH;
    });

    ctx.font = '9px "IBM Plex Sans",system-ui,sans-serif';
    ctx.fillStyle = getCSSVar('--text3') || '#888';
    ctx.fillText(d.date ?? '', tx, H - 1);
    ctx.restore();
  }
}

/* ── Cumulative FII/DII chart — running totals from start of range = 0 ── */
function drawCumulativeFlowsChart(canvas, data, visible, crosshairDate) {
  if (!canvas) return;
  if (!data || data.length < 2) { clearCanvas(canvas); return; }
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  canvas.width  = rect.width  * dpr;
  canvas.height = rect.height * dpr;
  const ctx = canvas.getContext('2d');
  ctx.scale(dpr, dpr);
  const W = rect.width, H = rect.height;
  const pad = { l:8, r:8, t:6, b:6 };

  // Build cumulative series — each series starts at 0 on day 0
  const cumKeys = Object.keys(FLOW_KEYS).filter(s => visible[s]);
  const cumSeries = {};
  cumKeys.forEach(s => { cumSeries[s] = []; });
  const running = {};
  cumKeys.forEach(s => { running[s] = 0; });

  data.forEach(d => {
    cumKeys.forEach(s => {
      const v = d[FLOW_KEYS[s]];
      if (v != null) running[s] += v;
      cumSeries[s].push(running[s]);
    });
  });

  // Overall min/max across all cumulative series
  let maxV = 0, minV = 0;
  cumKeys.forEach(s => {
    cumSeries[s].forEach(v => {
      maxV = Math.max(maxV, v);
      minV = Math.min(minV, v);
    });
  });
  if (maxV === 0 && minV === 0) { clearCanvas(canvas); return; }

  const totalRange = (maxV - minV) || 1;
  const innerH = H - pad.t - pad.b;
  const zeroY  = pad.t + (maxV / totalRange) * innerH;
  const py_v   = v => zeroY - (v / totalRange) * innerH;

  ctx.clearRect(0, 0, W, H);

  // Zero line
  ctx.beginPath(); ctx.setLineDash([4, 3]);
  ctx.strokeStyle = 'rgba(128,128,128,0.4)'; ctx.lineWidth = 0.8;
  ctx.moveTo(pad.l, zeroY); ctx.lineTo(W - pad.r, zeroY); ctx.stroke(); ctx.setLineDash([]);
  ctx.fillStyle = getCSSVar('--text3') || '#888';
  ctx.font = '9px "IBM Plex Sans",system-ui,sans-serif'; ctx.textAlign = 'right';
  ctx.fillText('0', W - pad.r - 2, zeroY - 2);

  const n = data.length;
  const step = (W - pad.l - pad.r) / Math.max(n - 1, 1);

  // Draw each series as a filled area line
  cumKeys.forEach(s => {
    const vals = cumSeries[s];
    const color = FLOW_COLORS[s];

    // Fill area
    ctx.beginPath();
    ctx.moveTo(pad.l, zeroY);
    vals.forEach((v, i) => ctx.lineTo(pad.l + i * step, py_v(v)));
    ctx.lineTo(pad.l + (n - 1) * step, zeroY);
    ctx.closePath();
    ctx.globalAlpha = 0.18;
    ctx.fillStyle = color;
    ctx.fill();
    ctx.globalAlpha = 1;

    // Line
    ctx.beginPath();
    vals.forEach((v, i) => {
      const x = pad.l + i * step;
      const y = py_v(v);
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.globalAlpha = 0.9;
    ctx.stroke();
    ctx.globalAlpha = 1;
  });

  // Crosshair
  if (!crosshairDate) return;
  const ci = data.findIndex(d => d.date === crosshairDate);
  if (ci < 0) return;
  const cx = pad.l + ci * step;

  ctx.save();
  ctx.beginPath(); ctx.setLineDash([3, 3]);
  ctx.strokeStyle = getCSSVar('--text3') || '#888'; ctx.lineWidth = 0.8;
  ctx.moveTo(cx, pad.t); ctx.lineTo(cx, H - pad.b); ctx.stroke(); ctx.setLineDash([]);

  // Value labels — one per series
  const d = data[ci];
  const onRight = cx < W * 0.55;
  const tx = cx + (onRight ? 6 : -6);
  const lineH = 11, boxPad = 4;
  const visKeys = cumKeys.filter(s => data[ci][FLOW_KEYS[s]] != null || running[s] !== 0);

  ctx.font = 'bold 9px "IBM Plex Mono",monospace';
  let maxTW = 0;
  visKeys.forEach(s => {
    const cv = cumSeries[s][ci];
    const rounded = Math.round(cv);
    const sign = rounded >= 0 ? '+' : '';
    const formatted = rounded.toLocaleString('en-IN');
    const tw = ctx.measureText(`${s.toUpperCase()} ${sign}${formatted}cr`).width;
    if (tw > maxTW) maxTW = tw;
  });
  const boxW = maxTW + 14;
  const boxX = onRight ? cx + 4 : cx - 4 - boxW;
  const boxH = visKeys.length * lineH + boxPad * 2 - 2;
  const boxY = pad.t + 2;

  ctx.save();
  ctx.globalAlpha = 0.70;
  ctx.fillStyle = getCSSVar('--bg') || '#ffffff';
  ctx.fillRect(boxX, boxY, boxW, boxH);
  ctx.globalAlpha = 1;
  ctx.restore();

  ctx.textAlign = onRight ? 'left' : 'right';
  let ty = pad.t + boxPad + lineH - 2;
  visKeys.forEach(s => {
    const cv = cumSeries[s][ci];
    const rounded = Math.round(cv);
    const sign = rounded >= 0 ? '+' : '';
    const formatted = rounded.toLocaleString('en-IN');
    ctx.fillStyle = FLOW_COLORS[s];
    ctx.fillText(`${s.toUpperCase()} ${sign}${formatted}cr`, tx, ty);
    ty += lineH;
  });

  ctx.font = '9px "IBM Plex Sans",system-ui,sans-serif';
  ctx.fillStyle = getCSSVar('--text3') || '#888';
  ctx.fillText(d.date ?? '', tx, H - 1);
  ctx.restore();
}

/* ── Chart wrapper — shared crosshair, resize observer, legend ── */
/* Reusable pill-mode switcher used in C4, C5, C6 chart headers */
function ModePills({ options, active, onChange, scheme = 'amber' }) {
  const activeBg = scheme === 'blue' ? 'var(--accent-blue)' : 'var(--amber-ink)';
  return (
    <span style={{ display:'inline-flex', gap:2 }}>
      {options.map(({ value, label: lbl }) => (
        <button key={value}
          onClick={e => { e.stopPropagation(); onChange(value); }}
          style={{
            fontSize:11, padding:'1px 5px', borderRadius:3, border:'none',
            cursor:'pointer', fontWeight:600, lineHeight:1.4,
            background: active===value ? activeBg : 'var(--surface-2)',
            color:      active===value ? '#fff'    : 'var(--ink-3)',
          }}>
          {lbl}
        </button>
      ))}
    </span>
  );
}

function Chart({ id, draw, dates, crosshairDate, onCrosshairChange, label, rangeLabel,
                 controls = null, legend = [], placeholder = null, hasData = true, height = 130 }) {
  const canvasRef = useRef(null);

  useEffect(() => { draw(canvasRef.current); }, [draw]);
  useEffect(() => {
    const ob = new ResizeObserver(() => draw(canvasRef.current));
    if (canvasRef.current) ob.observe(canvasRef.current);
    return () => ob.disconnect();
  }, [draw]);

  const handleMouseMove = useCallback((e) => {
    if (!canvasRef.current || !dates?.length) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const n = dates.length;
    const padX = 8;
    const i = Math.round((mouseX - padX) / (rect.width - padX * 2) * (n - 1));
    const d = dates[Math.max(0, Math.min(n - 1, i))];
    if (d) onCrosshairChange?.(d);
  }, [dates, onCrosshairChange]);

  const handleMouseLeave = useCallback(() => onCrosshairChange?.(null), [onCrosshairChange]);

  return (
    <div className="cc" style={{ borderRadius:10, position:'relative' }}>
      <div className="ct">
        <span style={{ flex:1 }}>{label}</span>
        {controls && <span style={{ flexShrink:0, marginRight:6 }}>{controls}</span>}
        <span className="cs" style={{ flexShrink:0 }}>{rangeLabel}</span>
      </div>
      {legend.length > 0 && (
        <div className="cl">
          {legend.map((l, i) => (
            <div key={i} className="li"
              onClick={l.onClick}
              style={l.onClick ? { cursor:'pointer', userSelect:'none' } : {}}>
              {l.dot
                ? <div className="ld" style={{ background:l.color, opacity: l.inactive ? 0.25 : 1 }}/>
                : <div className="ll" style={{ background:l.color, opacity: l.inactive ? 0.25 : 1,
                                               borderStyle:l.dash?'dashed':'solid' }}/>}
              <span style={{ opacity: l.inactive ? 0.4 : 1 }}>{l.label}</span>
            </div>
          ))}
        </div>
      )}
      <div style={{ position:'relative', cursor:'crosshair' }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}>
        <canvas ref={canvasRef} style={{ width:'100%', height, display:'block' }}/>
        {!hasData && placeholder && (
          <div style={{
            position:'absolute', inset:0,
            display:'flex', alignItems:'center', justifyContent:'center',
            background:'rgba(244,241,234,.55)', /* --paper at 55% opacity */
            backdropFilter:'blur(2px)', borderRadius:8,
          }}>
            <span style={{ fontSize:11, color:'var(--ink-3)', textAlign:'center', lineHeight:1.5 }}>
              {placeholder}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── AI interpretation ── */
/* PivotBar and AISection (NiftyAISection) are imported from ../ai/NiftyAISection
   Any UI changes should be made there so they propagate to both NiftyTab and AISummaryTab. */

/* ── Prediction grid ── */
const SCALE_BG = {
  1: { bg:'rgba(136,30,16,.55)',  label:'Strong Bearish' },
  2: { bg:'rgba(136,30,16,.28)',  label:'Mild Bearish' },
  3: { bg:'rgba(138,90,0,.22)',   label:'Neutral' },
  4: { bg:'rgba(26,106,26,.28)',  label:'Mild Bullish' },
  5: { bg:'rgba(26,106,26,.55)',  label:'Strong Bullish' },
};

const SCALE_FALLBACK = { bg:'var(--surface-2)', label:'—' };
const scaleBg = (v) => SCALE_BG[v] || SCALE_FALLBACK;

/* ── Match cell background by absolute distance ── */
function matchCellBg(dist) {
  if (dist === null) return 'var(--surface-2)';
  if (dist === 0)    return 'rgba(138,110,60,0.12)';  // tan/neutral — exact match
  if (dist === 1)    return 'rgba(166,120,40,0.38)';  // amber — off by 1
  if (dist === 2)    return 'rgba(140,95,20,0.58)';   // louder amber — off by 2
  return 'rgba(120,80,10,0.72)';                      // filled/dark amber — off by 3+
}
function matchTextOpacity(dist) {
  if (dist === null) return 0;
  return 0.85;
}

function PredGrid({ history }) {
  // Only show days with actual market data (no weekends / future predictions)
  // Limit to last 30 trading days that have a valid actual scale value
  const tradingDays = (history || [])
    .filter(d => d.actual >= 1 && d.actual <= 5)
    .slice(-30);
  const n = tradingDays.length;
  const cols = `repeat(${Math.max(n, 1)}, 1fr)`;

  /* Pre-compute per-day match data so we can render flat row grids */
  const days = tradingDays.map(d => {
    const pa = scaleBg(d.actual), pp = scaleBg(d.pred);
    const valid = d.pred >= 1 && d.pred <= 5 && d.actual >= 1 && d.actual <= 5;
    const signedDist  = valid ? (d.pred - d.actual) : null;
    const absDist     = signedDist !== null ? Math.abs(signedDist) : null;
    const signedLabel = signedDist === null ? ''
                      : signedDist === 0 ? '0'
                      : signedDist > 0 ? `+${signedDist}` : `${signedDist}`;
    const dirNote = signedDist === null ? 'Pending'
                  : signedDist > 0 ? '(AI too bullish)'
                  : signedDist < 0 ? '(AI too bearish)' : '(exact match)';
    return {
      dateSlice: (d.date || '').slice(5),
      actualBg: pa.bg, actualLabel: pa.label, actualVal: d.actual,
      predBg:   pp.bg, predLabel:   pp.label, predVal:   d.pred,
      matchBg:  matchCellBg(absDist),
      absDist,
      textOpacity: matchTextOpacity(absDist),
      signedLabel,
      tooltip: [
        (d.date || '').slice(5),
        `Actual: ${pa.label} (${d.actual ?? '?'})`,
        `AI Pred: ${pp.label} (${d.pred ?? '?'})`,
        `Off by ${signedLabel} ${dirNote}`,
      ].join('\n'),
    };
  });

  /* Row component — renders one horizontal strip of N cells */
  const Row = ({ lbl, lblBold, children }) => (
    <div style={{ display:'grid', gridTemplateColumns:'60px 1fr', gap:8, marginBottom:3, alignItems:'center' }}>
      <span style={{
        fontSize:9, fontWeight: lblBold ? 800 : 700,
        textTransform:'uppercase', letterSpacing:'.05em',
        color: lblBold ? 'var(--ink)' : 'var(--ink-3)',
      }}>{lbl}</span>
      <div style={{ display:'grid', gridTemplateColumns:cols, gap:1.5 }}>
        {children}
      </div>
    </div>
  );

  return (
    <>
      {/* Legend — 9.5px with 9×9 swatches */}
      <div style={{ display:'flex', gap:14, flexWrap:'wrap', alignItems:'center', marginBottom:8 }}>
        {[5,4,3,2,1].map(lvl => (
          <div key={lvl} style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
            <div style={{ width:9, height:9, borderRadius:1, background:SCALE_BG[lvl].bg, flexShrink:0 }}/>
            <span style={{ fontSize:9.5, color:'var(--ink-3)' }}>{SCALE_BG[lvl].label}</span>
          </div>
        ))}
      </div>

      {/* Actual row */}
      <Row lbl="Actual">
        {days.map((d, i) => (
          <div key={i} style={{ height:11, borderRadius:1, background:d.actualBg }}
            title={`${d.dateSlice} · Actual: ${d.actualLabel}`}/>
        ))}
      </Row>

      {/* AI Pred row */}
      <Row lbl="AI Pred">
        {days.map((d, i) => (
          <div key={i} style={{ height:11, borderRadius:1, background:d.predBg, opacity:.75 }}
            title={`${d.dateSlice} · AI Pred: ${d.predLabel}`}/>
        ))}
      </Row>

      {/* Match row — dashed separator, taller cells */}
      <div style={{
        display:'grid', gridTemplateColumns:'60px 1fr', gap:8, alignItems:'center',
        marginTop:8, paddingTop:8, paddingBottom:6,
        borderTop:'1px dashed var(--hair-strong)',
        borderBottom:'1px dashed var(--hair-strong)',
      }}>
        <span style={{ fontSize:9, fontWeight:800, textTransform:'uppercase', letterSpacing:'.05em', color:'var(--ink)' }}>
          Match
        </span>
        <div style={{ display:'grid', gridTemplateColumns:cols, gap:1.5, alignItems:'stretch' }}>
          {days.map((d, i) => (
            <div key={i}
              title={d.tooltip}
              style={{
                height:24, borderRadius:2,
                background:d.matchBg,
                display:'flex', alignItems:'center', justifyContent:'center',
                fontFamily:'var(--font-data)', fontSize:9.5, fontWeight:600,
                color: d.absDist === 0 ? 'var(--ink-2)' : 'var(--paper)', cursor:'help',
                transition:'transform .08s ease, box-shadow .1s ease',
                position:'relative',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'scale(1.5)';
                e.currentTarget.style.boxShadow = '0 3px 10px rgba(0,0,0,0.2)';
                e.currentTarget.style.zIndex = '10';
                const num = e.currentTarget.querySelector('.mn');
                if (num) num.style.opacity = '0.95';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.zIndex = '1';
                const num = e.currentTarget.querySelector('.mn');
                if (num) num.style.opacity = String(d.textOpacity);
              }}
            >
              <span className="mn" style={{ opacity:d.textOpacity, transition:'opacity .1s', pointerEvents:'none', letterSpacing:'-0.02em' }}>
                {d.signedLabel}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ fontSize:9.5, fontStyle:'italic', color:'var(--ink-3)', marginTop:6 }}>
        Match cell shows signed distance (AI − Actual). Positive = AI too bullish, negative = AI too bearish. Hover any cell for day detail.
      </div>
    </>
  );
}

/* ── Stat pill — threshold-colored header pills ── */
function StatPill({ label, value, level }) {
  // level: 'healthy' | 'watch' | 'needs_work' | 'neutral'
  const bg    = level === 'healthy'    ? 'rgba(30,100,30,0.10)'
              : level === 'watch'      ? 'rgba(166,139,77,0.12)'
              : level === 'needs_work' ? 'rgba(166,106,0,0.22)'
              : 'var(--surface-2)';
  const color = level === 'healthy'    ? 'var(--up-field-ink)'
              : level === 'watch'      ? '#8a6a35'
              : level === 'needs_work' ? '#6b4e1a'
              : 'var(--ink-3)';
  return (
    <span style={{
      fontSize:10, fontWeight:600, fontFamily:'var(--font-data)',
      padding:'2px 8px', borderRadius:10,
      background:bg, color, whiteSpace:'nowrap',
    }}>
      {label}: {value}
    </span>
  );
}

/* ── Combined AI Performance & Diagnostics section ── */
function DiagnosticsSection({ history, diagnostics, diagExpanded, setDiagExpanded }) {
  const [activeView, setActiveView] = useState(
    () => localStorage.getItem('nifty.diag.activeView') || 'overall'
  );
  const [diagWindow, setDiagWindow] = useState(60);

  const setView = v => { setActiveView(v); localStorage.setItem('nifty.diag.activeView', v); };

  const d = diagnostics || {};
  const pctW1   = d.pct_within_1;
  const pctOff2 = d.pct_off_by_2_plus;
  const mean    = d.mean_signed_miss;
  const nbw     = d.n_buckets_needs_work ?? 0;

  const w1BaselineNeutral = d.w1_baseline_neutral;
  const w1BaselinePersist = d.w1_baseline_persist;
  const w1BestBaseline = (w1BaselineNeutral != null && w1BaselinePersist != null)
    ? Math.max(w1BaselineNeutral, w1BaselinePersist)
    : (w1BaselineNeutral ?? w1BaselinePersist ?? null);
  const w1Edge = (pctW1 != null && w1BestBaseline != null)
    ? Math.round(pctW1 - w1BestBaseline)
    : null;
  const w1Label = pctW1 == null ? '—'
    : w1BestBaseline != null
    ? `${pctW1}% · base ${w1BestBaseline}% · ${w1Edge >= 0 ? '+' : ''}${w1Edge}pts`
    : `${pctW1}%`;

  const w1Level  = pctW1 == null ? 'neutral' : pctW1 > 60 ? 'healthy' : pctW1 >= 40 ? 'watch' : 'needs_work';
  const o2Level  = pctOff2 == null ? 'neutral' : pctOff2 < 15 ? 'healthy' : pctOff2 <= 25 ? 'watch' : 'needs_work';
  const mmLevel  = mean == null ? 'neutral' : Math.abs(mean) < 0.4 ? 'healthy' : Math.abs(mean) <= 0.8 ? 'watch' : 'needs_work';
  const nbwLevel = nbw === 0 ? 'healthy' : nbw === 1 ? 'watch' : 'needs_work';

  const meanLabel = mean == null ? '—' : `${mean > 0 ? '+' : ''}${mean.toFixed(2)} (${mean > 0.1 ? 'slight bull bias' : mean < -0.1 ? 'slight bear bias' : 'balanced'})`;

  const perBucket = d.per_bucket || {};
  const focusHint = d.focus_hint || null;

  return (
    <>
      {/* Eyebrow heading row — outside the card box */}
      <div style={{
        fontSize:11, fontWeight:700, letterSpacing:'.07em', textTransform:'uppercase',
        color:'var(--ink-3)', margin:'14px 0 8px',
        display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:6,
      }}>
        <button
          onClick={() => {
            const next = !diagExpanded;
            setDiagExpanded(next);
            localStorage.setItem('nifty.diag.expanded', next ? '1' : '0');
          }}
          style={{ background:'none', border:'none', cursor:'pointer', padding:0,
            fontSize:11, fontWeight:700, letterSpacing:'.07em', textTransform:'uppercase', color:'var(--ink-3)',
            display:'flex', alignItems:'center', gap:5,
          }}
        >
          {diagExpanded ? '▾' : '▸'} AI Performance &amp; Diagnostics
        </button>
      </div>

      <div className="card" style={{ padding:0, marginBottom:14, overflow:'hidden' }}>
      {/* Expanded body */}
      {diagExpanded && (
        <div style={{ padding:'12px 12px 14px' }}>
          {/* Toggle + stat pills on same row */}
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12, flexWrap:'wrap' }}>
            <div className="seg">
              <button className={`sbtn${activeView==='overall'?' on':''}`} onClick={() => setView('overall')}>Overall</button>
              <button className={`sbtn${activeView==='per_bucket'?' on':''}`} onClick={() => setView('per_bucket')}>Per-Bucket</button>
            </div>
            {activeView === 'overall' && (
              <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                <StatPill label="Within 1" value={w1Label} level={w1Level} />
                <StatPill label="Off 2+" value={pctOff2 != null ? `${pctOff2}%` : '—'} level={o2Level} />
                <StatPill label="Mean miss" value={meanLabel} level={mmLevel} />
                {nbw > 0 && <StatPill label="Buckets low" value={nbw} level={nbwLevel} />}
              </div>
            )}
          </div>

          {activeView === 'overall' && (
            <PredGrid history={history} />
          )}

          {activeView === 'per_bucket' && (
            <>
              {/* Header: intro text left, window dropdown right */}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12, gap:8, flexWrap:'wrap' }}>
                <span style={{ fontSize:10.5, color:'var(--ink-2)', fontStyle:'italic' }}>
                  Per-bucket accuracy diagnoses where the prompt needs work.
                </span>
                <span style={{ fontSize:10, color:'var(--ink-3)', whiteSpace:'nowrap', display:'flex', alignItems:'center', gap:4 }}>
                  Window:
                  <select
                    value={diagWindow}
                    onChange={e => setDiagWindow(Number(e.target.value))}
                    style={{
                      fontSize:10, fontFamily:'var(--font-ui)',
                      border:'.5px solid var(--hair-strong)', background:'var(--surface)',
                      color:'var(--ink)', padding:'3px 6px', borderRadius:3,
                    }}
                  >
                    <option value={30}>Last 30 days</option>
                    <option value={60}>Last 60 days</option>
                    <option value={90}>Last 90 days</option>
                  </select>
                </span>
              </div>

              {/* Per-bucket rows — grid: 140px name | 52px pct | 16px n= | 1fr bar | 90px flag */}
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {['fii','pcr','vix','breadth','fut','global'].map(k => {
                  const b = perBucket[k];
                  if (!b) return null;
                  const pct = b.agreement_pct;

                  // Amber-based color scheme: dark amber = needs work, light amber = watch, neutral = healthy
                  const barColor  = b.flag === 'NEEDS_WORK' ? 'rgba(166,106,0,0.70)'
                                  : b.flag === 'WATCH'      ? 'rgba(166,139,77,0.55)'
                                  : b.flag === 'HEALTHY'    ? 'rgba(120,120,120,0.35)'
                                  : 'var(--surface-2)';
                  const pctColor  = b.flag === 'NEEDS_WORK' ? '#6b4e1a'
                                  : b.flag === 'WATCH'      ? '#8a6a35'
                                  : 'var(--ink-3)';
                  const flagBg    = b.flag === 'NEEDS_WORK' ? 'rgba(166,106,0,0.18)'
                                  : b.flag === 'WATCH'      ? 'rgba(166,139,77,0.12)'
                                  : 'rgba(120,120,120,0.08)';
                  const flagColor = b.flag === 'NEEDS_WORK' ? '#6b4e1a'
                                  : b.flag === 'WATCH'      ? '#8a6a35'
                                  : 'var(--ink-3)';
                  const isBuilding = b.flag === 'BUILDING';
                  const flagLabel = isBuilding              ? 'Building'
                                  : b.flag === 'NO_DATA'    ? 'No data'
                                  : b.flag === 'NEEDS_WORK' ? 'Needs Work'
                                  : b.flag === 'WATCH'      ? 'Watch'
                                  : 'Healthy';

                  return (
                    <div key={k} style={{ display:'grid', gridTemplateColumns:'140px 48px 24px 1fr 90px', gap:0, columnGap:8, alignItems:'center' }}>
                      {/* Name — bold, uppercase */}
                      <span style={{
                        fontSize:10, fontWeight:700, textTransform:'uppercase',
                        letterSpacing:'.05em', color:'var(--ink)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis',
                      }}>
                        {b.name}
                      </span>
                      {/* Pct — right-aligned mono */}
                      <span style={{
                        fontSize:11, fontFamily:'var(--font-data)', fontWeight:700,
                        color:pctColor, textAlign:'right', whiteSpace:'nowrap',
                      }}>
                        {pct != null ? `${pct}%` : '—'}
                      </span>
                      {/* n=X — separate column for alignment */}
                      <span style={{ fontSize:9, color:'var(--ink-3)', fontWeight:400, whiteSpace:'nowrap', paddingLeft:3 }}>
                        {b.n_days > 0 ? `n=${b.n_days}` : ''}
                      </span>
                      {/* Bar */}
                      <div style={{ height:8, background:'var(--surface-2)', borderRadius:4, overflow:'hidden' }}>
                        {pct != null && (
                          <div style={{ height:'100%', width:`${pct}%`, background:barColor, borderRadius:4, transition:'width .3s ease' }}/>
                        )}
                      </div>
                      {/* Flag pill */}
                      <span style={{
                        fontSize:9, fontWeight:700, letterSpacing:'.04em', textTransform:'uppercase',
                        padding:'2px 7px', borderRadius:3, textAlign:'center',
                        background:flagBg, color:flagColor,
                        whiteSpace:'nowrap',
                      }}>
                        {flagLabel}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Where to focus */}
              {focusHint && (
                <div style={{ marginTop:12, paddingTop:8, borderTop:'.5px dashed var(--hair-strong)' }}>
                  <div style={{ fontSize:9.5, color:'var(--ink-3)', fontStyle:'italic', lineHeight:1.5 }}>
                    💡 <strong style={{ color:'var(--ink-2)', fontStyle:'normal' }}>Least predictive factor:</strong>{' '}
                    {focusHint.bucket} at {focusHint.agreement_pct}% Factor Agreement (n={focusHint.n_days}).
                    {' '}Down-weight this bucket's signal until agreement recovers above 58%.
                  </div>
                </div>
              )}
              {Object.keys(perBucket).length > 0 && Object.values(perBucket).every(b => b.flag === 'NO_DATA') && (
                <div style={{ fontSize:11, color:'var(--ink-3)', fontStyle:'italic', textAlign:'center', padding:'8px 0' }}>
                  Factor Agreement builds as reports accumulate. No gradeable history yet.
                </div>
              )}
            </>
          )}
        </div>
      )}
      </div>
    </>
  );
}

/* ── Table helpers ── */
function fmtW(v, decimals = 1) {
  if (v == null) return '—';
  return v.toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

const _MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function fmtDateDMY(s) {
  if (!s) return '—';
  const parts = s.split('-');
  if (parts.length !== 3) return s;
  const [y, m, d] = parts;
  return `${d.padStart(2,'0')}-${_MONTHS[parseInt(m, 10) - 1]}-${y}`;
}

const TBL  = { fontSize:11, fontFamily:'var(--font-ui)' };
const MONO = { ...TBL, fontFamily:'var(--font-data)' };

/* ── Data table ── */
function DataTable({ data, weeklyData, weeklyLoading, monthlyData, monthlyLoading, loading, equityFiiBusy, setEquityFiiBusy, onRefreshHistory }) {
  const [view, setView] = useState('daily');
  const dailyRows = useMemo(() => [...data].reverse(), [data]);

  return (
    <>
      <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.07em', textTransform:'uppercase', color:'var(--ink-3)', margin:'14px 0 8px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span>Data Table</span>
          <span style={{ fontSize:11, color:'var(--ink-3)', fontWeight:400, textTransform:'none', letterSpacing:0 }}>
            {view === 'daily'
              ? (loading && data.length === 0 ? 'Loading…' : `${data.length} days (Jan 2024+)`)
              : view === 'weekly'
              ? `${weeklyData?.length ?? 0} weeks (Jan 2024+)`
              : `${monthlyData?.length ?? 0} months (Jan 2024+)`}
          </span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <div className="seg">
            <button className={`sbtn${view==='daily'?' on':''}`} onClick={() => setView('daily')}>Daily</button>
            <button className={`sbtn${view==='weekly'?' on':''}`} onClick={() => setView('weekly')}>Weekly</button>
            <button className={`sbtn${view==='monthly'?' on':''}`} onClick={() => setView('monthly')}>Monthly</button>
          </div>
        </div>
      </div>

      {view === 'daily' ? (
        <div className="tw" style={{ maxHeight:'50vh', overflowY:'auto' }}>

          <table style={TBL}>
            <thead>
              <tr className="grp">
                <th colSpan={2}>Date</th>
                <th colSpan={6} style={{ borderLeft:'.5px solid var(--hair)' }}>NIFTY</th>
                <th colSpan={3} style={{ borderLeft:'.5px solid var(--hair)' }}>Futures</th>
                <th colSpan={3} style={{ borderLeft:'.5px solid var(--hair)' }}>Breadth</th>
                <th colSpan={2} style={{ borderLeft:'.5px solid var(--hair)' }}>VIX</th>
                <th colSpan={2} style={{ borderLeft:'.5px solid var(--hair)' }}>PCR</th>
                <th colSpan={3} style={{ borderLeft:'.5px solid var(--hair)' }}>DII/FII</th>
                <th colSpan={3} style={{ borderLeft:'.5px solid var(--hair)' }}>% Net Long (Index F&O)</th>
              </tr>
              <tr className="col">
                <th className="thl">Date</th><th className="thl">Exp</th>
                <th style={{ borderLeft:'.5px solid var(--hair)' }}>Open</th>
                <th style={{ color:'var(--up-field-ink)' }}>High</th>
                <th style={{ color:'var(--down-field-ink)' }}>Low</th>
                <th style={{ color:'var(--ink)', fontWeight:800 }}>Close</th>
                <th>Chg</th><th>Chg%</th>
                <th style={{ borderLeft:'.5px solid var(--hair)' }}>FUT OI</th>
                <th>OI Chg%</th><th>Build</th>
                <th style={{ borderLeft:'.5px solid var(--hair)' }}>Adv</th><th>Dec</th><th>A/D</th>
                <th style={{ borderLeft:'.5px solid var(--hair)' }}>VIX</th><th>VChg</th>
                <th style={{ borderLeft:'.5px solid var(--hair)' }}>Near</th><th>N+Nx</th>
                <th style={{ borderLeft:'.5px solid var(--hair)' }}>DII Stks</th><th>FII Stks</th><th>FII Fut</th>
                <th style={{ borderLeft:'.5px solid var(--hair)' }}>FII F+O</th><th>FII+PRO</th><th>Client</th>
              </tr>
            </thead>
            <tbody>
              {dailyRows.length === 0 ? (
                <tr><td colSpan={24} style={{ ...TBL, textAlign:'center', padding:'24px 0', color:'var(--ink-3)' }}>
                  {loading ? 'Loading data…' : 'No data yet — backfill running in background'}
                </td></tr>
              ) : dailyRows.map((row, i) => {
                const pos = row.chgPct >= 0;
                // CHG = close − previous trading day's close (day-over-day, same basis as CHG%)
                const prevClose = dailyRows[i + 1]?.close;
                const chgAbs    = (row.close != null && prevClose != null)
                  ? (row.close - prevClose).toFixed(0)
                  : null;
                return (
                  <tr key={i} className={row.isExpiry ? 'exp-row' : ''}>
                    <td className="thl" style={{ ...MONO, color:row.isExpiry?'var(--amber-ink)':'var(--ink)' }}>{fmtDateDMY(row.date)}</td>
                    <td className="thl" style={TBL}>{row.isExpiry ? '📅' : ''}</td>
                    <td style={{ ...MONO, borderLeft:'.5px solid var(--hair)' }}>{fmtP(row.open)}</td>
                    <td style={{ ...MONO, color:'var(--up-field-ink)' }}>{fmtP(row.high)}</td>
                    <td style={{ ...MONO, color:'var(--down-field-ink)' }}>{fmtP(row.low)}</td>
                    <td style={{ ...MONO, fontWeight:700, color:'var(--ink)' }}>{fmtP(row.close)}</td>
                    <td style={{ ...TBL, color:pos?'var(--up-field-ink)':'var(--down-field-ink)' }}>{chgAbs != null ? `${pos?'+':''}${chgAbs}` : '—'}</td>
                    <td style={{ ...TBL, color:pos?'var(--up-field-ink)':'var(--down-field-ink)', fontWeight:600 }}>{pos?'+':''}{row.chgPct?.toFixed(2)}%</td>
                    <td style={{ ...TBL, borderLeft:'.5px solid var(--hair)' }}>
                      {row.futOI ? `${(row.futOI / 100000).toFixed(1)}L` : '—'}
                    </td>
                    <td style={{ ...TBL, color:row.oiChgPct >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)' }}>
                      {row.oiChgPct != null ? `${row.oiChgPct >= 0?'+':''}${row.oiChgPct?.toFixed(1)}%` : '—'}
                    </td>
                    <td style={TBL}>
                      {row.buildUp ? <span className={`bu-${row.buildUp}`}>{row.buildUp}</span> : <span style={{ color:'var(--ink-3)' }}>—</span>}
                    </td>
                    <td style={{ ...TBL, borderLeft:'.5px solid var(--hair)', color:'var(--adv)' }}>{row.advDec?.adv || '—'}</td>
                    <td style={{ ...TBL, color:'var(--dec)' }}>{row.advDec?.dec || '—'}</td>
                    <td style={{ ...TBL, color:row.advDec?.ratio>=1?'var(--up-field-ink)':'var(--down-field-ink)', fontWeight:600 }}>{row.advDec?.ratio?.toFixed(2) || '—'}</td>
                    <td style={{ ...TBL, borderLeft:'.5px solid var(--hair)', color:row.vix>18?'var(--down-field-ink)':'var(--ink)' }}>{row.vix ?? '—'}</td>
                    <td style={{ ...TBL, color:row.vixChg>=0?'var(--up-field-ink)':'var(--down-field-ink)' }}>
                      {row.vixChg != null ? `${row.vixChg>=0?'+':''}${row.vixChg?.toFixed(2)}` : '—'}
                    </td>
                    <td style={{ ...TBL, borderLeft:'.5px solid var(--hair)', color:row.pcrNear>=1?'var(--up-field-ink)':'var(--down-field-ink)', fontWeight:500 }}>{row.pcrNear ?? '—'}</td>
                    <td style={TBL}>{row.pcrNearNext ?? '—'}</td>
                    <td style={{ ...TBL, borderLeft:'.5px solid var(--hair)', color:row.dii!=null?(row.dii>=0?'var(--up-field-ink)':'var(--down-field-ink)'):'var(--ink-3)' }}>
                      {row.dii != null ? `${row.dii>=0?'+':'-'}${Math.abs(row.dii).toLocaleString('en-IN',{minimumFractionDigits:1,maximumFractionDigits:1})}` : '—'}
                    </td>
                    <td style={{ ...TBL, color:row.fii!=null?(row.fii>=0?'var(--up-field-ink)':'var(--down-field-ink)'):'var(--ink-3)' }}>
                      {row.fii != null ? `${row.fii>=0?'+':'-'}${Math.abs(row.fii).toLocaleString('en-IN',{minimumFractionDigits:1,maximumFractionDigits:1})}` : '—'}
                    </td>
                    <td style={{ ...TBL, color:row.fiiNetFut!=null?(row.fiiNetFut>=0?'var(--up-field-ink)':'var(--down-field-ink)'):'var(--ink-3)', fontWeight:500 }}>
                      {row.fiiNetFut != null ? `${row.fiiNetFut>=0?'+':'-'}${Math.abs(row.fiiNetFut).toLocaleString('en-IN',{minimumFractionDigits:1,maximumFractionDigits:1})}` : '—'}
                    </td>
                    <td style={{ ...TBL, borderLeft:'.5px solid var(--hair)', color:row.fiiNetFutOpt!=null?(row.fiiNetFutOpt>=0?'var(--up-field-ink)':'var(--down-field-ink)'):'var(--ink-3)', fontWeight:600 }}>
                      {row.fiiNetFutOpt != null ? `${row.fiiNetFutOpt>=0?'+':''}${row.fiiNetFutOpt?.toFixed(1)}` : '—'}
                    </td>
                    <td style={{ ...TBL, color:row.fiiProFutOpt!=null?(row.fiiProFutOpt>=0?'var(--up-field-ink)':'var(--down-field-ink)'):'var(--ink-3)' }}>
                      {row.fiiProFutOpt != null ? `${row.fiiProFutOpt>=0?'+':''}${row.fiiProFutOpt?.toFixed(1)}` : '—'}
                    </td>
                    <td style={{ ...TBL, color:row.clientFutOpt!=null?(row.clientFutOpt>=0?'var(--up-field-ink)':'var(--down-field-ink)'):'var(--ink-3)' }}>
                      {row.clientFutOpt != null ? `${row.clientFutOpt>=0?'+':''}${row.clientFutOpt?.toFixed(1)}` : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : view === 'weekly' ? (
        weeklyLoading ? (
          <div className="card" style={{ padding:16, textAlign:'center', color:'var(--ink-3)', fontSize:11 }}>
            Loading weekly data…
          </div>
        ) : !weeklyData || weeklyData.length === 0 ? (
          <div className="card" style={{ padding:16, textAlign:'center', color:'var(--ink-3)', fontSize:11 }}>
            Weekly data not yet available — backfill running in background.
          </div>
        ) : (
          <div className="tw" style={{ maxHeight:'60vh', overflowY:'auto' }}>
            <table style={TBL}>
              <thead>
                <tr className="grp">
                  <th colSpan={3}>Week</th>
                  <th colSpan={4} style={{ borderLeft:'.5px solid var(--hair)' }}>Price</th>
                  <th colSpan={2} style={{ borderLeft:'.5px solid var(--hair)' }}>Intraday</th>
                  <th colSpan={4} style={{ borderLeft:'.5px solid var(--hair)' }}>From Entry</th>
                  <th colSpan={2} style={{ borderLeft:'.5px solid var(--hair)' }}>Ratio</th>
                </tr>
                <tr className="col">
                  <th className="thl">#</th>
                  <th className="thl">Entry Date</th>
                  <th className="thl">Expiry</th>
                  <th style={{ borderLeft:'.5px solid var(--hair)' }}>Start</th>
                  <th>End</th><th>Change</th><th>|Chg|</th>
                  <th style={{ borderLeft:'.5px solid var(--hair)', color:'var(--up-field-ink)' }}>Max</th>
                  <th style={{ color:'var(--down-field-ink)' }}>Min</th>
                  <th style={{ borderLeft:'.5px solid var(--hair)' }}>Range</th>
                  <th>IV Entry</th>
                  <th style={{ color:'var(--up-field-ink)' }}>Max-S</th>
                  <th style={{ color:'var(--down-field-ink)' }}>Min-S</th>
                  <th style={{ borderLeft:'.5px solid var(--hair)' }}>H/L</th>
                  <th>L/H</th>
                </tr>
              </thead>
              <tbody>
                {weeklyData.map((w, i) => {
                  const up = w.change >= 0;
                  const hlUp = w.ratioHL > 1;
                  return (
                    <tr key={i}>
                      <td className="thl" style={{ ...TBL, color:'var(--ink-3)' }}>{w.weekNum}</td>
                      <td className="thl" style={{ ...MONO, color:'var(--ink-3)' }}>{fmtDateDMY(w.startDate)}</td>
                      <td className="thl" style={MONO}>{fmtDateDMY(w.closeDate)}</td>
                      <td style={{ ...MONO, borderLeft:'.5px solid var(--hair)' }}>{fmtP(w.start)}</td>
                      <td style={MONO}>{fmtP(w.end)}</td>
                      <td style={{ ...TBL, color:up?'var(--up-field-ink)':'var(--down-field-ink)', fontWeight:600 }}>
                        {w.change != null ? `${up?'+':''}${fmtW(w.change)}` : '—'}
                      </td>
                      <td style={{ ...TBL, fontWeight:600 }}>{fmtW(w.absChange)}</td>
                      <td style={{ ...MONO, borderLeft:'.5px solid var(--hair)', color:'var(--up-field-ink)' }}>{fmtP(w.weeklyMax)}</td>
                      <td style={{ ...MONO, color:'var(--down-field-ink)' }}>{fmtP(w.weeklyMin)}</td>
                      <td style={{ ...TBL, borderLeft:'.5px solid var(--hair)' }}>{fmtW(w.range)}</td>
                      <td style={{ ...TBL, color:w.ivEntry > 18 ? 'var(--down-field-ink)' : 'var(--ink)' }}>{fmtW(w.ivEntry, 2)}</td>
                      <td style={{ ...TBL, color:'var(--up-field-ink)' }}>{fmtW(w.maxStart)}</td>
                      <td style={{ ...TBL, color:'var(--down-field-ink)' }}>{fmtW(w.minStart)}</td>
                      <td style={{ ...TBL, borderLeft:'.5px solid var(--hair)', color:hlUp?'var(--up-field-ink)':'var(--down-field-ink)', fontWeight:600 }}>{fmtW(w.ratioHL, 2)}</td>
                      <td style={{ ...TBL, color:'var(--down-field-ink)' }}>{fmtW(w.ratioLH, 2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      ) : view === 'monthly' ? (
        monthlyLoading ? (
          <div className="card" style={{ padding:16, textAlign:'center', color:'var(--ink-3)', fontSize:11 }}>
            Loading monthly data…
          </div>
        ) : !monthlyData || monthlyData.length === 0 ? (
          <div className="card" style={{ padding:16, textAlign:'center', color:'var(--ink-3)', fontSize:11 }}>
            Monthly data not yet available.
          </div>
        ) : (
          <div className="tw" style={{ maxHeight:'60vh', overflowY:'auto' }}>
            <table style={TBL}>
              <thead>
                <tr className="grp">
                  <th colSpan={3}>Month</th>
                  <th colSpan={4} style={{ borderLeft:'.5px solid var(--hair)' }}>Price</th>
                  <th colSpan={2} style={{ borderLeft:'.5px solid var(--hair)' }}>Intraday</th>
                  <th colSpan={4} style={{ borderLeft:'.5px solid var(--hair)' }}>From Entry</th>
                  <th colSpan={2} style={{ borderLeft:'.5px solid var(--hair)' }}>Ratio</th>
                  <th colSpan={3} style={{ borderLeft:'.5px solid var(--hair)' }}>DII/FII Cum. (₹Cr)</th>
                </tr>
                <tr className="col">
                  <th className="thl">#</th>
                  <th className="thl">Entry Date</th>
                  <th className="thl">Expiry</th>
                  <th style={{ borderLeft:'.5px solid var(--hair)' }}>Start</th>
                  <th>End</th><th>Change</th><th>|Chg|</th>
                  <th style={{ borderLeft:'.5px solid var(--hair)', color:'var(--up-field-ink)' }}>High</th>
                  <th style={{ color:'var(--down-field-ink)' }}>Low</th>
                  <th style={{ borderLeft:'.5px solid var(--hair)' }}>Range</th>
                  <th>IV Entry</th>
                  <th style={{ color:'var(--up-field-ink)' }}>Max-S</th>
                  <th style={{ color:'var(--down-field-ink)' }}>Min-S</th>
                  <th style={{ borderLeft:'.5px solid var(--hair)' }}>H/L</th>
                  <th>L/H</th>
                  <th style={{ borderLeft:'.5px solid var(--hair)', color:'var(--up-field-ink)' }}>DII</th>
                  <th style={{ color:'var(--down-field-ink)' }}>FII</th>
                  <th>FII FUT</th>
                </tr>
              </thead>
              <tbody>
                {monthlyData.map((m, i) => {
                  const up = m.change >= 0;
                  const hlUp = m.ratioHL > 1;
                  const fmtCr = v => v == null ? '—' : `${v >= 0 ? '+' : ''}${Math.round(v).toLocaleString('en-IN')}`;
                  const [yr, mo] = (m.monthKey || '').split('-');
                  const monthLabel = mo && yr ? `${['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][parseInt(mo)]} ${yr}` : m.monthKey;
                  return (
                    <tr key={i} title={monthLabel}>
                      <td className="thl" style={{ ...TBL, color:'var(--ink-3)' }}>{m.monthNum}</td>
                      <td className="thl" style={{ ...MONO, color:'var(--ink-3)' }}>{fmtDateDMY(m.startDate)}</td>
                      <td className="thl" style={MONO}>{fmtDateDMY(m.closeDate)}</td>
                      <td style={{ ...MONO, borderLeft:'.5px solid var(--hair)' }}>{fmtP(m.start)}</td>
                      <td style={MONO}>{fmtP(m.end)}</td>
                      <td style={{ ...TBL, color:up?'var(--up-field-ink)':'var(--down-field-ink)', fontWeight:600 }}>
                        {m.change != null ? `${up?'+':''}${fmtW(m.change)}` : '—'}
                      </td>
                      <td style={{ ...TBL, fontWeight:600 }}>{fmtW(m.absChange)}</td>
                      <td style={{ ...MONO, borderLeft:'.5px solid var(--hair)', color:'var(--up-field-ink)' }}>{fmtP(m.monthHigh)}</td>
                      <td style={{ ...MONO, color:'var(--down-field-ink)' }}>{fmtP(m.monthLow)}</td>
                      <td style={{ ...TBL, borderLeft:'.5px solid var(--hair)' }}>{fmtW(m.range)}</td>
                      <td style={{ ...TBL, color: m.ivEntry > 18 ? 'var(--down-field-ink)' : 'var(--ink)' }}>{fmtW(m.ivEntry, 2)}</td>
                      <td style={{ ...TBL, color:'var(--up-field-ink)' }}>{fmtW(m.maxStart)}</td>
                      <td style={{ ...TBL, color:'var(--down-field-ink)' }}>{fmtW(m.minStart)}</td>
                      <td style={{ ...TBL, borderLeft:'.5px solid var(--hair)', color:hlUp?'var(--up-field-ink)':'var(--down-field-ink)', fontWeight:600 }}>{fmtW(m.ratioHL, 2)}</td>
                      <td style={{ ...TBL, color:'var(--down-field-ink)' }}>{fmtW(m.ratioLH, 2)}</td>
                      <td style={{ ...TBL, borderLeft:'.5px solid var(--hair)', color: m.cumDii >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)' }}>{fmtCr(m.cumDii)}</td>
                      <td style={{ ...TBL, color: m.cumFii >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)' }}>{fmtCr(m.cumFii)}</td>
                      <td style={{ ...TBL, color: m.cumFiiFut >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)' }}>{fmtCr(m.cumFiiFut)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      ) : null}
    </>
  );
}

/* ── Main ── */
export default function NiftyTab() {
  const [range, setRange] = useState(90);
  const { open: openPanel } = useSidePanel();

  // ── Live data state ──
  const [niftyData,    setNiftyData]    = useState(() => _ssLoad(SS_KEY_HIST)   || []);
  const [weeklyData,   setWeeklyData]   = useState(() => _ssLoad(SS_KEY_WEEKLY));
  const [weeklyLoading,setWeeklyLoading]= useState(false);
  const [monthlyData,  setMonthlyData]  = useState(null);
  const [monthlyLoading,setMonthlyLoading] = useState(false);
  const [liveIndex,    setLiveIndex]    = useState(null);
  const [isTradingDay, setIsTradingDay] = useState(true); // backend-confirmed flag
  const [dataSource,   setDataSource]   = useState(() => _ssLoad(SS_KEY_HIST) ? 'live' : 'loading');
  const [loading,      setLoading]      = useState(() => !_ssLoad(SS_KEY_HIST));

  // ── Diagnostics state ──
  const [diagnostics,  setDiagnostics]  = useState(null);
  const [diagExpanded, setDiagExpanded] = useState(
    () => localStorage.getItem('nifty.diag.expanded') === '1'
  );

  // ── Shared crosshair state ──
  const [crosshairDate, setCrosshairDate] = useState(null);

  // ── Flows chart filter + mode state ──
  const [filterFlows, setFilterFlows] = useState({ dii: true, fii: true, fut: true });
  const toggleFlow = useCallback((key) => setFilterFlows(f => ({ ...f, [key]: !f[key] })), []);
  const [flowsMode, setFlowsMode] = useState('daily'); // 'daily' | 'cumulative'

  // ── C4 FII+PRO chart mode ──
  const [fiiMode, setFiiMode] = useState('vs_client'); // 'vs_client' | 'vs_ema'

  // ── C5 Breadth chart mode: daily EOD bars vs intraday 5-min line ──
  const [breadthMode,       setBreadthMode]       = useState('daily');   // 'daily' | 'intraday'
  const [intradayData,      setIntradayData]      = useState([]);
  const [intradaySessionDate, setIntradaySessionDate] = useState(null);  // date of shown session
  const [intradayIsLive,    setIntradayIsLive]    = useState(true);
  const [intradayLoading,   setIntradayLoading]   = useState(false);
  const [equityFiiBusy,     setEquityFiiBusy]     = useState(false);

  const [aiReport, setAiReport] = useState(null);

  // ── Fetch AI report ──
  useEffect(() => {
    getNiftyAI(30).then(res => {
      if (res?.sections) {
        // Flat response shape: { sections, prediction, date, history }
        setAiReport(res);
      } else if (res?.has_report && res?.sections) {
        setAiReport(res);
      }
    }).catch(() => {}); // silent — fall back to mock
  }, []);

  const ai = aiReport || NIFTY_AI;

  // ── Generate NIFTY report ──
  const [niftyGenerating, setNiftyGenerating] = useState(false);
  const [niftyGenMsg,     setNiftyGenMsg]     = useState('');

  const handleNiftyGenerate = async () => {
    setNiftyGenerating(true);
    setNiftyGenMsg('Generating… (20–60s)');
    const prevDate = aiReport?.date || '';
    try {
      await generateTabReport('nifty', null);
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const res = await getNiftyAI(30);
          if (res?.sections && (attempts > 3 || (res.date || '') !== prevDate)) {
            clearInterval(poll);
            setAiReport(res);
            setNiftyGenerating(false);
            setNiftyGenMsg('✓ Generated!');
            setTimeout(() => setNiftyGenMsg(''), 4000);
            return;
          }
        } catch {}
        if (attempts >= 25) {
          clearInterval(poll);
          setNiftyGenerating(false);
          setNiftyGenMsg('⚠ Timed out — refresh in a minute');
        }
      }, 6000);
    } catch (e) {
      setNiftyGenerating(false);
      setNiftyGenMsg(`Error: ${e.message}`);
    }
  };

  // ── Fetch intraday breadth when mode switches to intraday ──
  const applyIntradayRes = res => {
    if (res?.snapshots) {
      setIntradayData(res.snapshots);
      setIntradaySessionDate(res.session_date ?? null);
      setIntradayIsLive(res.is_live ?? true);
    }
  };
  useEffect(() => {
    if (breadthMode !== 'intraday') return;
    setIntradayLoading(true);
    getBreadthIntraday().then(applyIntradayRes).catch(() => {}).finally(() => setIntradayLoading(false));
    // Poll every 5 min while in intraday mode
    const iv = setInterval(() => {
      getBreadthIntraday().then(applyIntradayRes).catch(() => {});
    }, 5 * 60 * 1000);
    return () => clearInterval(iv);
  }, [breadthMode]);

  // ── Fetch ──
  const fetchHistory = useCallback((force = false) => {
    if (!force && _cacheValid(_cache.history)) {
      setNiftyData(_cache.history.data);
      setDataSource('live');
      return;
    }
    setLoading(true);
    getNiftyHistory(500).then(res => {
      if (res?.rows?.length > 0) {
        _cacheSet('history', res.rows);
        _ssSave(SS_KEY_HIST, res.rows);
        setNiftyData(res.rows);
        setDataSource('live');
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const fetchWeekly = useCallback((force = false) => {
    if (!force && _cacheValid(_cache.weekly)) {
      setWeeklyData(_cache.weekly.data);
      return;
    }
    setWeeklyLoading(true);
    getNiftyWeekly().then(res => {
      if (res?.weeks) {
        _cacheSet('weekly', res.weeks);
        _ssSave(SS_KEY_WEEKLY, res.weeks);
        setWeeklyData(res.weeks);
      }
    }).catch(() => {}).finally(() => setWeeklyLoading(false));
  }, []);

  useEffect(() => {
    fetchHistory();
    fetchWeekly();
    triggerNiftyBackfill().catch(() => {});
    // Monthly + diagnostics — fire once on mount
    setMonthlyLoading(true);
    getNiftyMonthly().then(res => {
      if (res?.months) setMonthlyData(res.months);
    }).catch(() => {}).finally(() => setMonthlyLoading(false));
    getNiftyDiagnostics(60).then(res => {
      if (res && !res.error) setDiagnostics(res);
    }).catch(() => {});
  }, [fetchHistory, fetchWeekly]);

  const handleRefresh = useCallback(() => {
    _cacheClear();
    fetchHistory(true);
    fetchWeekly(true);
  }, [fetchHistory, fetchWeekly]);

  // Live NIFTY LTP every 30s
  useEffect(() => {
    function fetchLive() {
      getIndices().then(res => {
        // API returns {indices:[...], is_trading_day:bool, ...}
        const items = Array.isArray(res) ? res : (res?.indices || []);
        if (res?.is_trading_day === false) setIsTradingDay(false);
        else if (res?.is_trading_day === true) setIsTradingDay(true);
        const nifty = items.find(r => r.id === 'NIFTY50');
        if (nifty?.price) setLiveIndex(nifty);
      }).catch(() => {});
    }
    fetchLive();
    const iv = setInterval(fetchLive, 30_000);
    return () => clearInterval(iv);
  }, []);

  const data       = useMemo(() => niftyData.slice(-range), [niftyData, range]);
  const rangeLabel = `Last ${range} days`;

  // Market status — gated on isTradingDay (holiday → force CLOSED)
  const [mktStatus, setMktStatus] = useState(computeMktStatus);
  useEffect(() => {
    const iv = setInterval(() => setMktStatus(computeMktStatus()), 60_000);
    return () => clearInterval(iv);
  }, []);
  const effectiveMktStatus = !isTradingDay ? 'CLOSED' : mktStatus;
  const isMarketLive = effectiveMktStatus === 'OPEN' && dataSource === 'live';

  // LTP display
  const histLatest = niftyData[niftyData.length - 1];
  const histPrev   = niftyData[niftyData.length - 2];
  const ltp = liveIndex?.price ?? histLatest?.close ?? 0;
  const pts = liveIndex?.chg   ?? (histLatest && histPrev ? +(histLatest.close - histPrev.close).toFixed(2) : 0);
  const pct = liveIndex?.pct   ?? histLatest?.chgPct ?? 0;
  const up  = pct >= 0;

  // "as of" label for closed state: last DB row date + time of last indices fetch
  const closedAsOf = (() => {
    const d = histLatest?.date;
    if (!d) return null;
    return new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', timeZone:'Asia/Kolkata' });
  })();

  const todayLabel = new Date().toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' });

  const SC = {
    'PRE-OPEN': { bg:'var(--dec)',           border:'transparent',         tx:'#fff' },
    'OPEN':     { bg:'rgba(26,106,26,.10)',   border:'rgba(26,106,26,.25)', tx:'var(--up-field-ink)' },
    'CLOSED':   { bg:'rgba(136,30,16,.10)',   border:'rgba(136,30,16,.25)', tx:'var(--down-field-ink)' },
  };
  const sc = SC[effectiveMktStatus] || SC['CLOSED'];

  // ── Chart data memos ──
  const safeData    = useMemo(() => data.filter(d => d.close != null), [data]);
  const fiiData     = useMemo(() => data.filter(d => d.fiiProFutOpt != null), [data]);
  const pcrData     = useMemo(() => data.filter(d => d.pcrNear != null), [data]);
  const vixData     = useMemo(() => data.filter(d => d.vix != null), [data]);
  const breadthData = useMemo(() => data.filter(d => {
    const adv = d.advDec?.adv ?? 0;
    const dec = d.advDec?.dec ?? 0;
    return adv + dec > 0;
  }), [data]);
  const flowsData   = useMemo(() => data.filter(d =>
    d.dii != null || d.fii != null || d.fiiNetFut != null
  ), [data]);

  // ── Draw callbacks — all depend on crosshairDate for shared cursor ──
  const drawC1 = useCallback(c => drawNiftyBuildup(c, safeData, crosshairDate),
    [safeData, crosshairDate]);

  const drawC2 = useCallback(c => {
    if (!vixData.length) return;
    drawLineChart(c, vixData.map(d => ({ v: d.vix, date: d.date })), {
      color:'#1a5aaa' /* --accent-blue */, areaFill:true,
      refLines:[{v:20,c:'var(--down-field-ink)',l:'20'},{v:15,c:'var(--amber-ink)',l:'15'}],
      crosshairDate,
    });
  }, [vixData, crosshairDate]);

  const drawC3 = useCallback(c => {
    if (!pcrData.length) { clearCanvas(c); return; }
    drawLineChart(c, pcrData.map(d => ({ v: d.pcrNear ?? 1, date: d.date })), {
      color: '#1a5aaa' /* --accent-blue */,
      refLines:[
        {v:1.2,c:'var(--up-field-ink)',l:'1.2'},
        {v:1.0,c:'rgba(128,128,128,0.6)',l:'1.0'},
        {v:0.8,c:'var(--down-field-ink)',l:'0.8'},
      ],
      secondLine: Object.assign(pcrData.map(d => ({ v: d.pcrNearNext ?? 1 })), { color:'var(--ink-3)' }),
      // Gradient zone fills: green above 1.2 (bullish), red below 0.8 (bearish)
      zones: [
        { threshold: 1.2, above: true,  color: '#3f6b4d' /* --up-field-ink */ },
        { threshold: 0.8, above: false, color: '#8f4a44' /* --down-field-ink */ },
      ],
      crosshairDate,
    });
  }, [pcrData, crosshairDate]);

  const drawC4 = useCallback(c => {
    if (!fiiData.length) { clearCanvas(c); return; }
    const primary = fiiData.map(d => ({ v: d.fiiProFutOpt ?? 0, date: d.date }));
    if (fiiMode === 'vs_ema') {
      const ema10 = computeEMA(primary.map(d => d.v), 10);
      drawLineChart(c, primary, {
        color:'#1a5aaa' /* --accent-blue */, refLines:[{v:0,c:'var(--hair-strong)',l:'0'}],
        secondLine: Object.assign(ema10.map(v => ({ v })), { color:'#c8922a' /* --amber */ }),
        crosshairDate,
      });
    } else {
      drawLineChart(c, primary, {
        color:'#1a5aaa' /* --accent-blue */, refLines:[{v:0,c:'var(--hair-strong)',l:'0'}],
        secondLine: Object.assign(fiiData.map(d => ({ v: d.clientFutOpt ?? 0 })), { color:'#c8922a' /* --amber */ }),
        crosshairDate,
      });
    }
  }, [fiiData, crosshairDate, fiiMode]);

  const drawC5 = useCallback(c => {
    if (breadthMode === 'intraday') {
      drawBreadthIntradayChart(c, intradayData, crosshairDate);
    } else {
      drawBreadthChart(c, breadthData, crosshairDate);
    }
  }, [breadthMode, intradayData, breadthData, crosshairDate]);

  const drawC6 = useCallback(c =>
    flowsMode === 'cumulative'
      ? drawCumulativeFlowsChart(c, flowsData, filterFlows, crosshairDate)
      : drawFlowsChart(c, flowsData, filterFlows, crosshairDate),
    [flowsData, filterFlows, crosshairDate, flowsMode]);

  // Flows chart legend items (clickable toggles)
  const c6Legend = useMemo(() => [
    { dot:true, color: FLOW_COLORS.dii, label:'DII',       inactive:!filterFlows.dii, onClick:()=>toggleFlow('dii') },
    { dot:true, color: FLOW_COLORS.fii, label:'FII Equity', inactive:!filterFlows.fii, onClick:()=>toggleFlow('fii') },
    { dot:true, color: FLOW_COLORS.fut, label:'FII Deriv',  inactive:!filterFlows.fut, onClick:()=>toggleFlow('fut') },
  ], [filterFlows, toggleFlow]);

  return (
    <div>
      {/* Header */}
      {(() => {
        const hasBias = !!ai?.sections?.bias?.label;
        const biasSections = ai?.sections || {};
        const { aligned, total } = hasBias ? computeAlignmentSummary(biasSections) : { aligned:0, total:6 };
        const biasLo = (ai?.sections?.bias?.label || '').toLowerCase();
        const biasColor = biasLo.includes('bull') ? 'var(--up-field-ink)'
                        : biasLo.includes('bear') ? 'var(--down-field-ink)'
                        : 'var(--amber-ink)';
        const genTime = ai?.created_at ? fmtIst(ai.created_at).replace('Generated ', '') : null;
        return (
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12, gap:8 }}>
        {/* NIFTY name + LTP — click opens SidePanel */}
        <div
          role="button"
          tabIndex={0}
          onClick={() => openPanel({ name: 'NIFTY50' })}
          onKeyDown={e => e.key === 'Enter' && openPanel({ name: 'NIFTY50' })}
          style={{ display:'inline-flex', alignItems:'center', gap:10, flexShrink:0, cursor:'pointer' }}
          title="Open NIFTY 50 detail panel">
          <span style={{ fontSize:13, fontWeight:700, color:'var(--ink)', fontFamily:'var(--font-ui)' }}>NIFTY</span>
          <div style={{ display:'inline-flex', alignItems:'center', gap:6, background:'var(--surface-2)', border:'.5px solid var(--hair)', borderRadius:6, padding:'3px 9px' }}>
            <span style={{ fontSize:11, fontWeight:700, letterSpacing:'.06em', background:sc.bg, color:sc.tx, border:`.5px solid ${sc.border}`, padding:'1px 5px', borderRadius:3 }}>{effectiveMktStatus}</span>
            <span style={{ fontSize:12, fontWeight:700, fontFamily:'var(--font-data)', color:'var(--ink)' }}>
              {ltp.toLocaleString('en-IN',{minimumFractionDigits:2,maximumFractionDigits:2})}
            </span>
            <span style={{ fontSize:11, fontWeight:600, fontFamily:'var(--font-data)', color:up?'var(--up-field-ink)':'var(--down-field-ink)', whiteSpace:'nowrap' }}>
              {up?'+':''}{pts.toFixed(2)} ({up?'+':''}{pct.toFixed(2)}%)
            </span>
          </div>
          {isMarketLive ? (
            <span className="nifty-live-badge" style={{ fontSize:11, color:'var(--up-field-ink)', opacity:.7, letterSpacing:'.04em' }}>LIVE</span>
          ) : closedAsOf ? (
            <span style={{ fontSize:10, color:'var(--ink-3)', letterSpacing:'.02em' }}>{closedAsOf}</span>
          ) : null}
        </div>

        {/* Center: bias summary — bias word + alignment count + generated-at */}
        {hasBias && (
          <div className="nifty-bias-center" style={{ flex:1, minWidth:0, display:'flex', justifyContent:'center', overflow:'hidden', padding:'0 8px' }}>
            <span
              title={`${ai.sections.bias.label} · ${aligned}/${total} aligned · ${genTime || '—'}`}
              style={{
                display:'inline-flex', alignItems:'center', gap:5,
                padding:'3px 10px', borderRadius:4,
                border:'.5px solid var(--hair)', background:'var(--surface-2)',
                maxWidth:'100%', overflow:'hidden',
              }}
            >
              <span style={{ fontSize:11, fontWeight:700, letterSpacing:'.05em', color:biasColor, whiteSpace:'nowrap' }}>
                {ai.sections.bias.label}
              </span>
              <span style={{ fontSize:10, color:'var(--hair-strong)' }}>·</span>
              <span style={{ fontSize:10, color:'var(--ink-2)', whiteSpace:'nowrap' }}>
                {aligned}/{total} aligned
              </span>
              {genTime && (
                <>
                  <span style={{ fontSize:10, color:'var(--hair-strong)' }}>·</span>
                  <span style={{ fontSize:10, color:'var(--ink-3)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                    {genTime}
                  </span>
                </>
              )}
            </span>
          </div>
        )}

        {/* Refresh + date (range toggle moved to chart section) */}
        <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
          <button
            onClick={handleRefresh}
            disabled={loading}
            title="Refresh data (clears 5-min cache)"
            style={{
              background:'none', border:'.5px solid var(--hair)', borderRadius:4,
              color:loading?'var(--ink-3)':'var(--amber-ink)', cursor:loading?'wait':'pointer',
              fontSize:11, padding:'2px 7px', lineHeight:1, fontWeight:600,
              transition:'opacity .15s',
            }}>
            {loading ? '…' : <RefreshIcon size={13} />}
          </button>
          <span style={{ fontSize:11, color:'var(--ink-3)' }}>{todayLabel}</span>
        </div>
      </div>
        );
      })()}

      {/* ── AI Interpretation ── */}
      <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.07em', textTransform:'uppercase', color:'var(--ink-3)', margin:'0 0 8px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:6 }}>
        <span>NIFTY Daily Prediction</span>
        <div style={{ display:'flex', alignItems:'center', gap:8, fontWeight:400, textTransform:'none', letterSpacing:0 }}>
          {ai?.date && (
            <span style={{ fontSize:11, color:'var(--ink-3)' }}>{ai.date}</span>
          )}
          {niftyGenMsg ? (
            <span style={{ fontSize:11, fontWeight:600,
              color: niftyGenMsg.startsWith('✓') ? 'var(--up-field-ink)'
                   : niftyGenMsg.startsWith('⚠') ? 'var(--amber-ink)' : 'var(--down-field-ink)' }}>
              {niftyGenMsg}
            </span>
          ) : ai?.created_at ? (
            <span style={{ fontSize:11, color:'var(--ink-3)' }}>{fmtIst(ai.created_at)}</span>
          ) : null}
          <button
            onClick={handleNiftyGenerate}
            disabled={niftyGenerating}
            style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:4,
              border:'.5px solid var(--hair-strong)', background:'var(--surface-2)', color:'var(--ink-2)',
              cursor: niftyGenerating ? 'default' : 'pointer', opacity: niftyGenerating ? 0.6 : 1 }}>
            {niftyGenerating ? '⏳' : '⚡ Generate'}
          </button>
        </div>
      </div>
      <NiftyAISection
        ai={ai}
        niftyData={niftyData}
        livePrice={liveIndex?.price}
        diagnostics={diagnostics}
        inputSnapshot={ai?.input_snapshot}
        marketSnapshot={ai?.market_snapshot}
      />

      {/* ── Combined AI Performance & Diagnostics ── */}
      <DiagnosticsSection
        history={ai.history}
        diagnostics={diagnostics}
        diagExpanded={diagExpanded}
        setDiagExpanded={setDiagExpanded}
      />

      {/* Charts section label + range toggle */}
      <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.07em', textTransform:'uppercase', color:'var(--ink-3)', margin:'0 0 8px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <span>Market Trend Charts</span>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <div className="seg">
            <button className={`sbtn${range===90?' on':''}`} onClick={() => setRange(90)}>90D</button>
            <button className={`sbtn${range===20?' on':''}`} onClick={() => setRange(20)}>20D</button>
          </div>
          <span style={{ fontWeight:400, fontSize:11, textTransform:'none', letterSpacing:0, color:'var(--ink-3)' }}>
            hover to sync cursor
          </span>
        </div>
      </div>

      {/* 3×2 Chart grid — 6 charts, shared crosshair */}
      <div className="chart-grid">

        {/* Row 1 — Price / VIX */}
        <Chart id="c1" draw={drawC1} label="NIFTY Close + Futures Build-Up" rangeLabel={rangeLabel}
          dates={safeData.map(d => d.date)}
          crosshairDate={crosshairDate} onCrosshairChange={setCrosshairDate}
          legend={[{dot:true,color:'#1a5aaa' /* --accent-blue */,label:'Price'},{dot:true,color:'var(--lb)',label:'LB'},{dot:true,color:'var(--sb)',label:'SB'},{dot:true,color:'var(--sc)',label:'SC'},{dot:true,color:'var(--lu)',label:'LU'}]}/>

        <Chart id="c2" draw={drawC2} label="India VIX" rangeLabel={rangeLabel}
          dates={vixData.map(d => d.date)}
          crosshairDate={crosshairDate} onCrosshairChange={setCrosshairDate}
          hasData={vixData.length > 0}
          legend={[{color:'#1a5aaa' /* --accent-blue */,label:'VIX'},{label:'20',color:'var(--down-field-ink)'},{label:'15',color:'var(--amber-ink)'}]}/>

        {/* Row 2 — PCR / FII+PRO% */}
        <Chart id="c3" draw={drawC3} label="PCR" rangeLabel={rangeLabel}
          dates={pcrData.map(d => d.date)}
          crosshairDate={crosshairDate} onCrosshairChange={setCrosshairDate}
          hasData={pcrData.length > 0}
          placeholder={'Populating — PCR data\nbuilds up daily via backfill'}
          legend={[
            {color:'#1a5aaa' /* --accent-blue */,label:'Near'},
            {color:'var(--ink-3)',dash:true,label:'Near+Next'},
            {label:'1.2',color:'var(--up-field-ink)'},
            {label:'0.8',color:'var(--down-field-ink)'},
          ]}/>

        <Chart id="c4" draw={drawC4} label="FII+PRO % Net Long" rangeLabel={rangeLabel}
          controls={<ModePills
            options={[{value:'vs_client',label:'vs Client'},{value:'vs_ema',label:'vs 10 EMA'}]}
            active={fiiMode} onChange={setFiiMode}/>}
          dates={fiiData.map(d => d.date)}
          crosshairDate={crosshairDate} onCrosshairChange={setCrosshairDate}
          hasData={fiiData.length > 0}
          placeholder={'Populating — FII/PRO data\nbuilds up via NSE backfill'}
          legend={fiiMode === 'vs_client'
            ? [{color:'#1a5aaa' /* --accent-blue */,label:'FII+PRO'},{color:'#c8922a' /* --amber */,dash:true,label:'Client'}]
            : [{color:'#1a5aaa' /* --accent-blue */,label:'FII+PRO'},{color:'#c8922a' /* --amber */,dash:true,label:'10 EMA'}]
          }/>

        {/* Row 3 — Breadth / Flows */}
        <Chart id="c5" draw={drawC5}
          label={breadthMode === 'daily' ? 'NIFTY 50 Breadth %' : (() => {
            if (intradayLoading) return 'NIFTY 50 Breadth — Intraday …';
            if (breadthMode === 'intraday' && intradaySessionDate) {
              const d = new Date(intradaySessionDate + 'T00:00:00');
              const label = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
              return `NIFTY 50 Breadth — ${intradayIsLive ? 'Live' : `Prior (${label})`}`;
            }
            return 'NIFTY 50 Breadth — Intraday';
          })()}
          controls={<ModePills
            options={[{value:'daily',label:'Daily'},{value:'intraday',label:'Intraday'}]}
            active={breadthMode} onChange={setBreadthMode}/>}
          rangeLabel={breadthMode === 'daily' ? rangeLabel : (intradayIsLive ? 'Today' : 'Last session')}
          dates={breadthMode === 'daily'
            ? breadthData.map(d => d.date)
            : intradayData.map(d => d.time)}
          crosshairDate={crosshairDate} onCrosshairChange={setCrosshairDate}
          hasData={breadthMode === 'daily' ? breadthData.length > 0 : intradayData.length > 0}
          placeholder={breadthMode === 'daily'
            ? 'Populating — breadth data\nbuilds daily at 16:05 IST'
            : 'No intraday data yet\nSnapshots taken every 5 min during market hours'}
          legend={breadthMode === 'daily'
            ? [
                {dot:true,color:'rgba(26,106,26,0.75)',label:'Advancing'},
                {dot:true,color:'rgba(136,30,16,0.75)',label:'Declining'},
              ]
            : [
                {dot:true,color:'#1a5aaa' /* --accent-blue */,label:'Adv%'},
                {color:'#c8922a' /* --amber */,dash:true,label:'20 EMA'},
              ]
          }/>

        <Chart id="c6" draw={drawC6}
          label={flowsMode === 'daily' ? 'DII + FII + FII Deriv Flows (₹Cr)' : 'DII + FII + FII Deriv — Cumulative (₹Cr)'}
          controls={<ModePills
            options={[{value:'daily',label:'Daily'},{value:'cumulative',label:'Cumul.'}]}
            active={flowsMode} onChange={setFlowsMode}/>}
          rangeLabel={rangeLabel}
          dates={flowsData.map(d => d.date)}
          crosshairDate={crosshairDate} onCrosshairChange={setCrosshairDate}
          hasData={flowsData.length > 0}
          placeholder={'Populating — FII/DII flow data\nbuilds daily via NSE backfill'}
          legend={c6Legend}/>

      </div>

      {/* Data table */}
      <DataTable
        data={niftyData}
        weeklyData={weeklyData}
        weeklyLoading={weeklyLoading}
        monthlyData={monthlyData}
        monthlyLoading={monthlyLoading}
        loading={loading}
        equityFiiBusy={equityFiiBusy}
        setEquityFiiBusy={setEquityFiiBusy}
        onRefreshHistory={() => fetchHistory(true)}
      />
    </div>
  );
}
